// @ts-nocheck
/**
 * Sales Order Form Drawer
 * Enhanced form with payment method, address autocomplete, and improved UI
 */

import * as React from 'react';
import { useState, useEffect, useMemo, useRef, useCallback } from 'react';
import {
  useTranslation,
  useIsRTL,
  useSelector,
  useDispatch,
  useForm,
  Controller,
  zodResolver,
  z,
} from '@spcc/core';
import {
  Button,
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  Input,
  Label,
  cn,
  BaseDrawer,
  Textarea,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  RadioGroup,
  RadioGroupItem,
  ArrowLeft,
  ArrowRight,
  Truck,
  User as UserIcon,
  toast,
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
  HoverCard,
  HoverCardContent,
  HoverCardTrigger,
  Popover,
  PopoverContent,
  PopoverTrigger,
  Command,
  CommandInput,
  CommandList,
  CommandEmpty,
  CommandGroup,
  CommandItem,
} from '@spcc/uikit';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@spcc/uikit';
import {
  FileText,
  Plus,
  X,
  Package,
  Save,
  UserCheck,
  Building2,
  ShoppingCart,
  CreditCard,
  Wallet,
  Landmark,
  Check,
  Copy,
  Info,
  QrCode,
  Loader2,
  ChevronLeft,
  ChevronRight,
  ChevronDown,
  ChevronUp,
  Search,
  Image as ImageIcon,
} from '@spcc/uikit';
import type {
  Contract,
  PackagingType,
  PaymentMethod,
} from '../../domain/types';
import { addressbookActions, selectCities } from '@spcc/domains/addressbook';
import type { City } from '@spcc/domains/addressbook';
import {
  ResourceManagementPanel,
  resourcesApi,
  driversApi,
  vehiclesApi,
} from '@spcc/domains/assets';
import type { Driver, Vehicle, Resource } from '@spcc/domains/assets';
import { skusApi } from '@spcc/domains/pricing';
import { productsApi } from '@spcc/domains/products';
import type { Category } from '@spcc/domains/products';
import { selectUserChannel, selectAuthUser } from '@spcc/domains/auth';
import { selectAllowedCityIds } from '@spcc/domains/portal-init';
import {
  selectSapBalances,
  selectSapBalancesLoading,
  selectSapBalancesError,
  financeActions,
} from '@spcc/domains/finance';
import { factoriesApi } from '@spcc/domains/factories';
import type { FactoryListItem } from '@spcc/domains/factories';
import { customerAccountsApi } from '@spcc/domains/customer-accounts';
import { tenantsApi } from '@spcc/domains/tenants';
import type { TenantRef } from '@spcc/domains/customer-accounts';
import { SubCustomerSelector, SubCustomerTypeBadge } from '@spcc/domains/sub-customers';
import type { SubCustomer } from '@spcc/domains/sub-customers';
import { selectSalesOrdersError } from '../../store/sales-orders.selectors';
import { salesOrdersActions } from '../../store/sales-orders.slice';
import { salesOrdersApi } from '../../infrastructure/api/sales-orders-api';
import { WaitTimeLine } from '../components/wait-time-line';
import {
  selectIsAdmin,
  selectSelectedTenant,
  adminContextActions,
} from '@spcc/domains/admin-context';

interface SalesOrderFormDrawerProps {
  contract: Contract | null;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onSave: (data: any) => void;
  isSaving?: boolean;
}

type BulkQuantityOption = '30' | '25' | 'other';
type DeliveryOptionType = 'assign';
type PaymentMethodType = PaymentMethod;

type DeliveryOptionSplitForm = {
  deliveryOption: DeliveryOptionType;
  isExworks: boolean;
  assignee: string;
  resourceId?: string;
  resourceDocumentId?: string;
};

interface OrderLineForm {
  id: string;
  salesOrderLineId?: string;
  salesOrderLineDocumentId?: string;
  skuId: string;
  variantId: string;
  productCode: string;
  packaging: PackagingType | null;
  bulkOption: BulkQuantityOption;
  quantity: string;
  splitBy: string;
  numberOfOrders: string;
  unitPrice: string;
  deliveryOption: DeliveryOptionType;
  isExworks: boolean;
  assignee: string;
  resourceId?: string;
  resourceDocumentId?: string;
  deliveryOptionSplits: DeliveryOptionSplitForm[];
}

const createEmptyLine = (): OrderLineForm => {
  return {
    id: String(Date.now()),
    salesOrderLineId: undefined,
    salesOrderLineDocumentId: undefined,
    skuId: '',
    variantId: '',
    productCode: '',
    packaging: null,
    bulkOption: '600',
    quantity: '',
    splitBy: '',
    numberOfOrders: '1',
    unitPrice: '',
    deliveryOption: 'assign',
    isExworks: true,
    assignee: '',
    resourceId: undefined,
    resourceDocumentId: undefined,
    deliveryOptionSplits: [],
  };
};

type VariantOption = {
  id: string;
  documentId?: string;
  strapiId?: string;
  name: string;
  skuCode?: string | null;
  variantCode?: string | null;
  packaging?: PackagingType | null;
  weightKg?: number | null;
  unitPrice?: number | null;
  minQuantity?: number | null;
  maxQuantity?: number | null;
  minOrderQuantity?: number | null;
  maxOrderQuantity?: number | null;
  isAvailable?: boolean | null;
  imageUrl?: string;
};

type SkuOption = {
  id: string;
  name: string;
  productCode: string;
  variants: VariantOption[];
  categoryId?: string;
};

function inferPackagingFromVariant(
  variant: { name?: string; skuCode?: string | null } | null
): PackagingType {
  const packagingValue =
    typeof (variant as any)?.packaging === 'string'
      ? (variant as any).packaging
      : null;
  if (packagingValue === 'bulk' || packagingValue === 'bags')
    return packagingValue;

  const variantCode =
    typeof (variant as any)?.variantCode === 'string'
      ? (variant as any).variantCode
      : null;
  const haystack = `${variantCode ?? ''} ${variant?.skuCode ?? ''} ${
    variant?.name ?? ''
  }`.toLowerCase();
  if (
    haystack.includes('bulk') ||
    haystack.includes('ton') ||
    haystack.includes('tons')
  )
    return 'bulk';
  return 'bags';
}

function normalizePackaging(value: unknown): PackagingType {
  const haystack = String(value ?? '').toLowerCase();
  if (
    haystack.includes('bulk') ||
    haystack.includes('ton') ||
    haystack.includes('tons')
  )
    return 'bulk';
  return 'bags';
}

function isPackagingType(value: unknown): value is PackagingType {
  return value === 'bags' || value === 'bulk';
}

function matchesVariantId(
  variant: VariantOption,
  candidateId: string
): boolean {
  const candidate = String(candidateId ?? '').trim();
  if (!candidate) return false;
  return (
    variant.id === candidate ||
    variant.documentId === candidate ||
    variant.strapiId === candidate
  );
}

function toNonEmptyString(value: unknown): string | null {
  const trimmed = String(value ?? '').trim();
  return trimmed ? trimmed : null;
}

function hasAssignTarget(
  value:
    | { assignee?: unknown; resourceId?: unknown; resourceDocumentId?: unknown }
    | null
    | undefined
): boolean {
  if (!value) return false;
  return (
    toNonEmptyString((value as any).assignee) != null ||
    toNonEmptyString((value as any).resourceId) != null ||
    toNonEmptyString((value as any).resourceDocumentId) != null
  );
}

function isRedundantDeliveryOptionSplit(
  line: OrderLineForm,
  split: DeliveryOptionSplitForm | null | undefined
): boolean {
  if (!split) return true;

  const splitOption = split.deliveryOption || 'assign';
  const defaultOption = line.deliveryOption || 'assign';
  if (splitOption !== defaultOption) return false;

  // Check if isExworks flag differs
  const splitIsExworks = split.isExworks ?? false;
  const defaultIsExworks = line.isExworks ?? false;
  if (splitIsExworks !== defaultIsExworks) return false;

  // Same delivery option and isExworks. Check if assignee/resource differs.
  const splitAssignee = toNonEmptyString(split.assignee);
  const defaultAssignee = toNonEmptyString(line.assignee);
  const differsAssignee = (splitAssignee ?? '') !== (defaultAssignee ?? '');

  const splitResourceId = toNonEmptyString(split.resourceId);
  const splitResourceDocumentId = toNonEmptyString(split.resourceDocumentId);
  const defaultResourceId = toNonEmptyString(line.resourceId);
  const defaultResourceDocumentId = toNonEmptyString(line.resourceDocumentId);

  const differsResource =
    (splitResourceId ?? '') !== (defaultResourceId ?? '') ||
    (splitResourceDocumentId ?? '') !== (defaultResourceDocumentId ?? '');

  return !(differsAssignee || differsResource);
}

function hashString(input: string): number {
  let hash = 0;
  for (let i = 0; i < input.length; i++) {
    hash = (hash << 5) - hash + input.charCodeAt(i);
    hash |= 0;
  }
  return Math.abs(hash);
}

function getDotColor(label: string): string {
  const hue = hashString(label || 'product') % 360;
  return `hsl(${hue} 75% 50%)`;
}

const SPLIT_EPSILON = 1e-9;
const SPLIT_PREVIEW_LIMIT = 6;
const SPLIT_EDITOR_PAGE_SIZE = 4;
const BULK_SPLIT_BY_OPTIONS = ['30', '25'] as const;
const BAG_SPLIT_BY_OPTIONS = ['600', '500'] as const;
const PACKAGING_OPTIONS: Array<{ value: PackagingType; labelKey: string }> = [
  { value: 'bulk', labelKey: 'products.bulk' },
  { value: 'bags', labelKey: 'products.bags' },
];

function getDefaultSplitByValue(packaging: PackagingType): string {
  return packaging === 'bulk'
    ? BULK_SPLIT_BY_OPTIONS[0]
    : BAG_SPLIT_BY_OPTIONS[0];
}

function getSplitByOptions(
  packaging: PackagingType | null | undefined
): readonly string[] {
  if (packaging === 'bulk') return BULK_SPLIT_BY_OPTIONS;
  if (packaging === 'bags') return BAG_SPLIT_BY_OPTIONS;
  return [];
}

function getSplitByPlaceholder(
  packaging: PackagingType | null | undefined,
  t: (key: string) => string
): string {
  if (packaging === 'bulk') return t('products.tons');
  if (packaging === 'bags') return t('products.bags');
  return t('products.form.packaging');
}

function formatSplitByOptionLabel(
  option: string,
  packaging: PackagingType | null | undefined,
  t: any
): string {
  if (packaging === 'bulk') return `${option} ${t('products.tons')}`;
  if (packaging === 'bags') return `${option} ${t('products.bags')}`;
  return option;
}

type PackagingVehicleType = 'mixer' | 'flatbed';
function getRequiredVehicleTypeForPackaging(
  packaging: PackagingType | null | undefined
): PackagingVehicleType | null {
  if (packaging === 'bulk') return 'mixer';
  if (packaging === 'bags') return 'flatbed';
  return null;
}

function formatVehicleTypeLabel(
  vehicleType: PackagingVehicleType | null,
  t: (key: string, fallback?: string) => string
): string {
  if (vehicleType === 'mixer') return t('assets.mixer', 'Mixer');
  if (vehicleType === 'flatbed') return t('assets.flatbed', 'Flatbed');
  return '';
}

function canSelectResourceForLine(
  line: OrderLineForm | null | undefined
): boolean {
  return Boolean(line && line.skuId);
}

function getPackagingForTarget(
  lines: OrderLineForm[],
  target: { lineId: string } | null
): PackagingType | null {
  if (!target) return null;
  const line = lines.find((item) => item.id === target.lineId);
  return line?.packaging ?? null;
}

type CategoryColorStyles = {
  item: React.CSSProperties;
  trigger: React.CSSProperties;
  accent: React.CSSProperties;
};

const DEFAULT_CATEGORY_COLOR = '#3b82f6';
const CATEGORY_BG_ALPHA = 0.12;
const CATEGORY_BORDER_ALPHA = 0.35;

function normalizeHexColor(value?: string | null): string | null {
  if (!value) return null;
  const trimmed = value.trim();
  if (!trimmed) return null;
  const withHash = trimmed.startsWith('#') ? trimmed : `#${trimmed}`;
  const lower = withHash.toLowerCase();
  if (!/^#([0-9a-f]{3}|[0-9a-f]{6})$/.test(lower)) return null;
  if (lower.length === 4) {
    const r = lower[1];
    const g = lower[2];
    const b = lower[3];
    return `#${r}${r}${g}${g}${b}${b}`;
  }
  return lower;
}

function hexToRgb(hex: string): { r: number; g: number; b: number } | null {
  const normalized = normalizeHexColor(hex);
  if (!normalized) return null;
  const value = parseInt(normalized.slice(1), 16);
  return {
    r: (value >> 16) & 255,
    g: (value >> 8) & 255,
    b: value & 255,
  };
}

function rgbaFromHex(hex: string, alpha: number): string {
  const rgb = hexToRgb(hex);
  if (!rgb) return `rgba(59, 130, 246, ${alpha})`;
  return `rgba(${rgb.r}, ${rgb.g}, ${rgb.b}, ${alpha})`;
}

// NOTE: Inline styles are used here intentionally to support backend-provided hex colors.
function getCategoryColorStyles(
  category: Category | null | undefined
): CategoryColorStyles {
  const normalized =
    normalizeHexColor(category?.color) ?? DEFAULT_CATEGORY_COLOR;
  return {
    item: { borderColor: rgbaFromHex(normalized, CATEGORY_BORDER_ALPHA) },
    trigger: { backgroundColor: rgbaFromHex(normalized, CATEGORY_BG_ALPHA) },
    accent: { backgroundColor: normalized },
  };
}

function getFullImageUrl(relativeUrl: string | undefined): string | undefined {
  if (!relativeUrl) return undefined;
  const normalizedRelativeUrl = relativeUrl.trim();
  if (!normalizedRelativeUrl) return undefined;

  // If it's already a full URL, return as is
  if (
    normalizedRelativeUrl.startsWith('http://') ||
    normalizedRelativeUrl.startsWith('https://')
  ) {
    return normalizedRelativeUrl;
  }

  const baseCandidates = [
    import.meta.env.VITE_API_URL,
    import.meta.env.VITE_GATEWAY_URL,
    typeof window !== 'undefined' ? window.location.origin : undefined,
  ];

  for (const baseCandidate of baseCandidates) {
    if (!baseCandidate) continue;
    try {
      const normalizedBase = new URL(
        baseCandidate,
        typeof window !== 'undefined' ? window.location.origin : undefined
      ).toString();
      return new URL(normalizedRelativeUrl, normalizedBase).toString();
    } catch {
      // Ignore invalid base candidate and try the next one.
    }
  }

  // Last resort keeps value usable in the browser as a relative path.
  return normalizedRelativeUrl.startsWith('/')
    ? normalizedRelativeUrl
    : `/${normalizedRelativeUrl}`;
}

function getMediaUrlCandidate(media: unknown): string | undefined {
  if (!media || typeof media !== 'object') return undefined;

  const mediaRecord = media as {
    url?: unknown;
    formats?: Record<string, { url?: string | null } | undefined> | null;
  };

  return (
    mediaRecord.formats?.large?.url ??
    mediaRecord.formats?.medium?.url ??
    mediaRecord.formats?.small?.url ??
    mediaRecord.formats?.thumbnail?.url ??
    (typeof mediaRecord.url === 'string' ? mediaRecord.url : undefined)
  );
}

function getPrimaryImageUrl(images: unknown): string | undefined {
  if (!Array.isArray(images) || images.length === 0) return undefined;
  const imageUrl = getMediaUrlCandidate(images[0]);
  return getFullImageUrl(imageUrl);
}

function getProductVariantImageUrl(
  product: { variants?: unknown } | null
): string | undefined {
  if (!product || !Array.isArray(product.variants)) return undefined;

  for (const variant of product.variants) {
    const imageUrl = getPrimaryImageUrl(
      (variant as { images?: unknown }).images
    );
    if (imageUrl) return imageUrl;
  }

  return undefined;
}

function getSkuVariantFallbackImageUrl(sku: SkuOption): string | undefined {
  return sku.variants.find((variant) => Boolean(variant.imageUrl))?.imageUrl;
}

function getHoverCardSide(isRTL: boolean): 'left' | 'right' {
  return isRTL ? 'left' : 'right';
}

function ProductImageHoverPreview({
  imageUrl,
  label,
}: {
  imageUrl?: string;
  label: string;
}) {
  return (
    <div className="space-y-2">
      <div className="flex items-center justify-center overflow-hidden rounded-xl border border-border bg-white p-2">
        {imageUrl ? (
          <img
            src={imageUrl}
            alt={label}
            className="h-auto w-auto max-h-[420px] max-w-[420px] object-contain"
            loading="lazy"
            decoding="async"
          />
        ) : (
          <div className="flex h-[220px] w-[320px] items-center justify-center text-5xl text-muted-foreground">
            <ImageIcon className="w-12 h-12" />
          </div>
        )}
      </div>
      <p className="text-xs font-medium text-foreground/70 truncate">{label}</p>
    </div>
  );
}

function renderProductHoverCard({
  trigger,
  imageUrl,
  label,
  isRTL,
}: {
  trigger: React.ReactElement;
  imageUrl?: string;
  label: string;
  isRTL: boolean;
}) {
  return (
    <HoverCard openDelay={200} closeDelay={100}>
      <HoverCardTrigger asChild>{trigger}</HoverCardTrigger>
      <HoverCardContent
        side={getHoverCardSide(isRTL)}
        align="start"
        sideOffset={12}
        className="w-auto max-w-[460px] p-3"
      >
        <ProductImageHoverPreview imageUrl={imageUrl} label={label} />
      </HoverCardContent>
    </HoverCard>
  );
}

function groupProductsByCategory(
  products: SkuOption[],
  categories: Category[]
) {
  // Create a map of category ID to category
  const categoryMap = new Map(categories.map((c) => [c.id, c]));

  // Group products by category
  const grouped = new Map<
    string,
    { category: Category; products: SkuOption[] }
  >();
  const uncategorized: SkuOption[] = [];

  products.forEach((product) => {
    if (product.categoryId && categoryMap.has(product.categoryId)) {
      const category = categoryMap.get(product.categoryId)!;
      if (!grouped.has(category.id)) {
        grouped.set(category.id, { category, products: [] });
      }
      grouped.get(category.id)!.products.push(product);
    } else {
      uncategorized.push(product);
    }
  });

  // Convert to array and add uncategorized if any
  const result = Array.from(grouped.values());

  if (uncategorized.length > 0) {
    result.push({
      category: {
        id: 'uncategorized',
        documentId: 'uncategorized',
        code: 'OTHER',
        name: { en: 'Other Products', ar: 'منتجات أخرى' },
        color: '#94a3b8',
        isActive: true,
        createdAt: '',
        updatedAt: '',
      },
      products: uncategorized,
    });
  }

  return result;
}

function getAssetLabel(asset: any): string | undefined {
  const licensePlate =
    typeof asset?.licensePlate === 'string' ? asset.licensePlate.trim() : '';
  if (licensePlate) return licensePlate;

  const plateNumber =
    typeof asset?.plateNumber === 'string' ? asset.plateNumber.trim() : '';
  if (plateNumber) return plateNumber;

  const assetNumber =
    typeof asset?.assetNumber === 'string' ? asset.assetNumber.trim() : '';
  if (assetNumber) return assetNumber;

  return undefined;
}

function mapResourceToOption(resource: any) {
  const id = String(resource?.id ?? '').trim();
  const name = String(resource?.name ?? '').trim();
  if (!id || !name) return null;

  const driverName =
    typeof resource?.driver?.name === 'string'
      ? resource.driver.name
      : typeof resource?.driverName === 'string'
      ? resource.driverName
      : undefined;

  const powerUnitLabel = getAssetLabel(
    resource?.vehicle ?? resource?.powerUnit
  );
  const trailerLabel = getAssetLabel(resource?.trailer);

  return {
    id,
    documentId: resource?.documentId ?? undefined,
    name,
    driverName,
    powerUnitLabel,
    trailerLabel,
  };
}

function roundQuantity(value: number): number {
  const rounded = Math.round(value * 1000) / 1000;
  return Math.abs(rounded) < SPLIT_EPSILON ? 0 : rounded;
}

type SplitPlan = {
  total: number;
  split: number | null;
  fullCount: number;
  remainder: number;
  requestedCount: number;
  count: number;
  generatedTotal: number;
  droppedQuantity: number;
  droppedCount: number;
  quantities: number[];
  droppedQuantities: number[];
  minOrderQuantity: number | null;
  maxOrderQuantity: number | null;
};

type OrderQuantityLimits = {
  minOrderQuantity: number | null;
  maxOrderQuantity: number | null;
};

type CreateConfirmationLineSummary = {
  lineId: string;
  productName: string;
  requestedQuantity: number;
  generatedQuantity: number;
  generatedOrders: number;
  droppedQuantity: number;
  droppedOrders: number;
  unit: string;
  minOrderQuantity: number | null;
  maxOrderQuantity: number | null;
};

type CreateConfirmationSummary = {
  lines: CreateConfirmationLineSummary[];
  totalRequestedQuantity: number;
  totalGeneratedQuantity: number;
  totalDroppedQuantity: number;
  totalGeneratedOrders: number;
  totalDroppedOrders: number;
};

function emptySplitPlan(limits?: OrderQuantityLimits): SplitPlan {
  return {
    total: 0,
    split: null,
    fullCount: 0,
    remainder: 0,
    requestedCount: 0,
    count: 0,
    generatedTotal: 0,
    droppedQuantity: 0,
    droppedCount: 0,
    quantities: [],
    droppedQuantities: [],
    minOrderQuantity: limits?.minOrderQuantity ?? null,
    maxOrderQuantity: limits?.maxOrderQuantity ?? null,
  };
}

function isQuantityWithinOrderLimits(
  quantity: number,
  limits: OrderQuantityLimits
): boolean {
  if (limits.minOrderQuantity != null && quantity < limits.minOrderQuantity) {
    return false;
  }
  if (limits.maxOrderQuantity != null && quantity > limits.maxOrderQuantity) {
    return false;
  }
  return true;
}

function getOrderQuantityLimitsForLine(
  line: OrderLineForm,
  skuOptions: SkuOption[]
): OrderQuantityLimits {
  const sku = skuOptions.find((option) => option.id === line.skuId);
  const variant = sku?.variants.find((item) =>
    matchesVariantId(item, line.variantId)
  );

  return {
    minOrderQuantity: variant?.minOrderQuantity ?? null,
    maxOrderQuantity: variant?.maxOrderQuantity ?? null,
  };
}

function computeSplitPlan(
  total: number,
  splitBy: number,
  limits: OrderQuantityLimits = {
    minOrderQuantity: null,
    maxOrderQuantity: null,
  }
): SplitPlan {
  const totalRounded = roundQuantity(total);
  if (!Number.isFinite(totalRounded) || totalRounded <= 0) {
    return emptySplitPlan(limits);
  }

  const splitRounded = roundQuantity(splitBy);
  let requestedQuantities: number[];
  let fullCount = 0;
  let remainder = totalRounded;
  let split: number | null = null;

  if (
    !Number.isFinite(splitRounded) ||
    splitRounded <= 0 ||
    splitRounded >= totalRounded
  ) {
    requestedQuantities = [totalRounded];
  } else {
    split = splitRounded;
    fullCount = Math.floor((totalRounded + SPLIT_EPSILON) / splitRounded);
    requestedQuantities = Array.from({ length: fullCount }, () => splitRounded);
    remainder = roundQuantity(totalRounded - fullCount * splitRounded);
    if (remainder > SPLIT_EPSILON) {
      requestedQuantities.push(remainder);
    }
  }

  const quantities = requestedQuantities.filter((quantity) =>
    isQuantityWithinOrderLimits(quantity, limits)
  );
  const droppedQuantities = requestedQuantities.filter(
    (quantity) => !isQuantityWithinOrderLimits(quantity, limits)
  );
  const generatedTotal = roundQuantity(
    quantities.reduce((sum, quantity) => sum + quantity, 0)
  );
  const droppedQuantity = roundQuantity(
    droppedQuantities.reduce((sum, quantity) => sum + quantity, 0)
  );

  return {
    total: totalRounded,
    split,
    fullCount,
    remainder,
    requestedCount: requestedQuantities.length,
    count: quantities.length,
    generatedTotal,
    droppedQuantity,
    droppedCount: droppedQuantities.length,
    quantities,
    droppedQuantities,
    minOrderQuantity: limits.minOrderQuantity,
    maxOrderQuantity: limits.maxOrderQuantity,
  };
}

type DeliveryCityOption = {
  id: string;
  documentId?: string;
  name: string;
  nameEn: string;
  nameAr?: string;
  countryId?: string;
};

function matchesCityOption(
  city: DeliveryCityOption,
  selectedCityId: string
): boolean {
  if (!selectedCityId) return false;
  return city.id === selectedCityId || city.documentId === selectedCityId;
}

function matchesFactoryOption(
  factory: FactoryListItem,
  selectedFactoryId: string
): boolean {
  if (!selectedFactoryId) return false;
  return (
    factory.id === selectedFactoryId || factory.documentId === selectedFactoryId
  );
}

function buildDeliveryCityOptions(
  cities: City[],
  allowedCityIds: string[] | undefined,
  selectedCityId: string
): DeliveryCityOption[] {
  const allowed = (allowedCityIds ?? []).filter(Boolean);
  const optionsByKey = new Map<string, DeliveryCityOption>();

  for (const city of cities) {
    if (!city?.id || !city?.name) continue;
    const allowedByPolicy = allowed.length === 0 || allowed.includes(city.id);
    const isSelected = matchesCityOption(
      {
        id: city.id,
        documentId: city.documentId,
        name: city.name,
        countryId: city.countryId,
      },
      selectedCityId
    );
    if (!allowedByPolicy && !isSelected) continue;

    const key = city.documentId || city.id;
    if (!optionsByKey.has(key)) {
      const nameEn = (city as any).nameEn ?? city.name;
      const nameAr = (city as any).nameAr;
      optionsByKey.set(key, {
        id: city.id,
        documentId: city.documentId,
        name: city.name,
        nameEn,
        nameAr,
        countryId: city.countryId,
      });
    }
  }

  return Array.from(optionsByKey.values()).sort((a, b) =>
    a.name.localeCompare(b.name)
  );
}

function isVariantAvailable(
  variant: VariantOption | null | undefined
): boolean {
  return variant?.isAvailable !== false;
}

function pickFirstAvailableVariant(
  variants: VariantOption[]
): VariantOption | undefined {
  return variants.find(isVariantAvailable) ?? variants[0];
}

function pickDefaultVariantByPackaging(
  variants: VariantOption[],
  packaging: PackagingType | null | undefined
): VariantOption | undefined {
  const matching = filterVariantsByPackaging(variants, packaging);
  return matching.find(isVariantAvailable) ?? matching[0];
}

function pickPreferredVariant(
  variants: VariantOption[],
  packaging: PackagingType | null | undefined
): VariantOption | undefined {
  return (
    pickDefaultVariantByPackaging(variants, packaging) ??
    pickFirstAvailableVariant(variants)
  );
}

function filterSkuOptionsByPackaging(
  options: SkuOption[],
  packaging: PackagingType | null | undefined
): SkuOption[] {
  if (!isPackagingType(packaging)) return options;
  return options.filter((option) =>
    option.variants.some(
      (variant) => inferPackagingFromVariant(variant) === packaging
    )
  );
}

function filterVariantsByPackaging(
  variants: VariantOption[],
  packaging: PackagingType | null | undefined
): VariantOption[] {
  if (!isPackagingType(packaging)) return variants;
  return variants.filter(
    (variant) => inferPackagingFromVariant(variant) === packaging
  );
}

function buildSplitQuantities(
  plan: SplitPlan,
  startIndex: number,
  limit: number
): number[] {
  if (plan.count <= 0) return [];
  const start = Math.max(0, startIndex);
  const safeLimit = Math.max(0, limit);
  const end = Math.min(plan.count, start + safeLimit);
  const quantities: number[] = [];

  for (let index = start; index < end; index += 1) {
    quantities.push(plan.quantities[index] ?? 0);
  }

  return quantities;
}

function formatQuantity(value: number): string {
  if (Number.isInteger(value)) return String(value);
  const rounded = roundQuantity(value);
  return String(rounded).replace(/\.?0+$/, '');
}

function calculateBagsTonsEquivalent(
  quantity: string,
  weightKg: number | null | undefined
): number {
  const qty = Number.parseFloat(quantity);
  const bagWeightKg = Number(weightKg);
  if (!Number.isFinite(qty) || qty <= 0) return 0;
  if (!Number.isFinite(bagWeightKg) || bagWeightKg <= 0) return 0;
  return (qty * bagWeightKg) / 1000;
}

function getSplitPlanForLine(
  line: OrderLineForm,
  skuOptions: SkuOption[] = []
): SplitPlan {
  const quantityValue = Number.parseFloat(line.quantity);
  const splitByValue = Number.parseFloat(line.splitBy);
  const limits = getOrderQuantityLimitsForLine(line, skuOptions);
  if (!Number.isFinite(quantityValue) || quantityValue <= 0) {
    return emptySplitPlan(limits);
  }
  if (!Number.isFinite(splitByValue) || splitByValue <= 0) {
    return emptySplitPlan(limits);
  }
  return computeSplitPlan(quantityValue, splitByValue, limits);
}

function buildCreateConfirmationSummary(
  lines: OrderLineForm[],
  skuOptions: SkuOption[],
  translate: (
    key: string,
    fallback?: unknown,
    options?: Record<string, unknown>
  ) => string
): CreateConfirmationSummary {
  const lineSummaries = lines.map((line) => {
    const sku = skuOptions.find((option) => option.id === line.skuId);
    const variant = sku?.variants.find((item) =>
      matchesVariantId(item, line.variantId)
    );
    const splitPlan = getSplitPlanForLine(line, skuOptions);
    const requestedQuantity = Number.parseFloat(line.quantity);
    const productName =
      (sku?.name || line.productCode || variant?.name || '').trim() ||
      translate('products.item', 'Product');

    return {
      lineId: line.id,
      productName,
      requestedQuantity: Number.isFinite(requestedQuantity)
        ? roundQuantity(requestedQuantity)
        : 0,
      generatedQuantity: splitPlan.generatedTotal,
      generatedOrders: splitPlan.count,
      droppedQuantity: splitPlan.droppedQuantity,
      droppedOrders: splitPlan.droppedCount,
      unit:
        line.packaging === 'bulk'
          ? translate('products.tons', 'tons')
          : translate('products.bags', 'bags'),
      minOrderQuantity: splitPlan.minOrderQuantity,
      maxOrderQuantity: splitPlan.maxOrderQuantity,
    };
  });

  return {
    lines: lineSummaries,
    totalRequestedQuantity: roundQuantity(
      lineSummaries.reduce((sum, line) => sum + line.requestedQuantity, 0)
    ),
    totalGeneratedQuantity: roundQuantity(
      lineSummaries.reduce((sum, line) => sum + line.generatedQuantity, 0)
    ),
    totalDroppedQuantity: roundQuantity(
      lineSummaries.reduce((sum, line) => sum + line.droppedQuantity, 0)
    ),
    totalGeneratedOrders: lineSummaries.reduce(
      (sum, line) => sum + line.generatedOrders,
      0
    ),
    totalDroppedOrders: lineSummaries.reduce(
      (sum, line) => sum + line.droppedOrders,
      0
    ),
  };
}

function buildDefaultDeliveryOptionSplit(
  line: OrderLineForm
): DeliveryOptionSplitForm {
  const deliveryOption = line.deliveryOption || 'assign';
  return {
    deliveryOption,
    isExworks: line.isExworks ?? false,
    assignee: line.assignee || '',
    resourceId: line.resourceId,
    resourceDocumentId: line.resourceDocumentId,
  };
}

function normalizeDeliveryOptionSplitEntry(
  entry: DeliveryOptionSplitForm | null | undefined,
  fallback: DeliveryOptionSplitForm
): DeliveryOptionSplitForm {
  if (!entry) return { ...fallback };
  const deliveryOption = entry.deliveryOption || fallback.deliveryOption;
  return {
    deliveryOption,
    isExworks: entry.isExworks ?? fallback.isExworks ?? false,
    // Keep split assignment explicit. Do not fall back to default assignment here,
    // otherwise clearing a split resource resurrects the line-level selection.
    assignee: entry.assignee ?? '',
    resourceId: entry.resourceId,
    resourceDocumentId: entry.resourceDocumentId,
  };
}

function getSplitEntry(
  line: OrderLineForm,
  index: number
): DeliveryOptionSplitForm {
  const fallback = buildDefaultDeliveryOptionSplit(line);
  const entry = Array.isArray(line.deliveryOptionSplits)
    ? line.deliveryOptionSplits[index]
    : undefined;
  return normalizeDeliveryOptionSplitEntry(entry, fallback);
}

function buildDeliveryOptionSplits(
  line: OrderLineForm,
  splitCount: number,
  existingSplits?: DeliveryOptionSplitForm[] | null
): DeliveryOptionSplitForm[] {
  if (splitCount <= 0) return [];
  const fallback = buildDefaultDeliveryOptionSplit(line);
  const existing = Array.isArray(existingSplits) ? existingSplits : [];
  const next: DeliveryOptionSplitForm[] = [];

  for (let index = 0; index < splitCount; index += 1) {
    next.push(normalizeDeliveryOptionSplitEntry(existing[index], fallback));
  }

  return next;
}

function buildDeliveryOptionSplitsPayload(
  line: OrderLineForm,
  splitCount: number
): Array<Record<string, unknown> | null> | undefined {
  if (splitCount <= 0) return undefined;

  const overrides = Array.isArray(line.deliveryOptionSplits)
    ? line.deliveryOptionSplits
    : [];
  const payload: Array<Record<string, unknown> | null> = Array.from(
    { length: splitCount },
    () => null
  );

  for (let index = 0; index < splitCount; index += 1) {
    const split = overrides[index];
    if (!split) continue;
    if (isRedundantDeliveryOptionSplit(line, split)) continue;

    const deliveryOption = split.deliveryOption || 'assign';
    const entry: Record<string, unknown> = {
      deliveryOption,
      isExworks: split.isExworks ?? false,
    };

    // Always include assignee data
    const assignee = toNonEmptyString(split.assignee);
    if (assignee) entry.assignee = assignee;
    if (split.resourceId) entry.resourceId = split.resourceId;
    if (split.resourceDocumentId)
      entry.resourceDocumentId = split.resourceDocumentId;

    payload[index] = entry;
  }

  return payload.some(Boolean) ? payload : undefined;
}

function pruneDeliveryOptionSplitOverrides(
  line: OrderLineForm
): DeliveryOptionSplitForm[] {
  const splits = Array.isArray(line.deliveryOptionSplits)
    ? [...line.deliveryOptionSplits]
    : [];
  let changed = false;

  for (let index = 0; index < splits.length; index += 1) {
    if (isRedundantDeliveryOptionSplit(line, splits[index])) {
      if (splits[index] != null) changed = true;
      splits[index] = undefined as any;
    }
  }

  // Trim trailing empty entries to keep payload/state minimal.
  while (splits.length > 0 && splits[splits.length - 1] == null) {
    splits.pop();
    changed = true;
  }

  return changed ? splits : line.deliveryOptionSplits || [];
}

function normalizeDeliveryOptionValue(value: unknown): DeliveryOptionType {
  if (typeof value !== 'string') return 'assign';
  const normalized = value.trim().toLowerCase();
  if (normalized === 'assign') return normalized;
  // Map 'exworks' to 'assign' since exworks is now a separate flag
  if (normalized === 'exworks') return 'assign';
  return 'assign';
}

const PAYMENT_METHODS: {
  type: PaymentMethodType;
  labelKey: string;
  icon: typeof CreditCard;
  descKey: string;
}[] = [
  {
    type: 'credit_limit',
    labelKey: 'salesOrders.creditLimit',
    icon: Wallet,
    descKey: 'salesOrders.creditLimitDesc',
  },
  {
    type: 'viban',
    labelKey: 'salesOrders.viban',
    icon: Landmark,
    descKey: 'salesOrders.vibanDesc',
  },
  {
    type: 'credit_card',
    labelKey: 'salesOrders.creditCard',
    icon: CreditCard,
    descKey: 'salesOrders.creditCardDesc',
  },
];

// Mock data - in production these would come from the API
const MOCK_VIBAN = 'SA44 2000 0001 2345 6789 0123';
const MOCK_SAVED_CARDS = [
  { id: 'card-1', last4: '4242', brand: 'visa', expiry: '12/26' },
  { id: 'card-2', last4: '8888', brand: 'mada', expiry: '03/27' },
];

// Simple QR Code SVG component
function QRCodeSVG({ value, size = 120 }: { value: string; size?: number }) {
  // Generate a deterministic pattern based on the value
  const cells: boolean[][] = [];
  const gridSize = 21;

  // Create a simple pattern based on hash
  let hash = 0;
  for (let i = 0; i < value.length; i++) {
    hash = (hash << 5) - hash + value.charCodeAt(i);
    hash = hash & hash;
  }

  for (let row = 0; row < gridSize; row++) {
    cells[row] = [];
    for (let col = 0; col < gridSize; col++) {
      // Finder patterns (corners)
      const isFinderPattern =
        (row < 7 && col < 7) || // top-left
        (row < 7 && col >= gridSize - 7) || // top-right
        (row >= gridSize - 7 && col < 7); // bottom-left

      if (isFinderPattern) {
        // Create finder pattern
        const inOuterBorder =
          row === 0 ||
          row === 6 ||
          col === 0 ||
          col === 6 ||
          (row >= gridSize - 7 &&
            (row === gridSize - 7 || row === gridSize - 1)) ||
          (col >= gridSize - 7 &&
            (col === gridSize - 7 || col === gridSize - 1));
        const inInnerSquare =
          (row >= 2 && row <= 4 && col >= 2 && col <= 4) ||
          (row >= 2 &&
            row <= 4 &&
            col >= gridSize - 5 &&
            col <= gridSize - 3) ||
          (row >= gridSize - 5 && row <= gridSize - 3 && col >= 2 && col <= 4);
        cells[row][col] = inOuterBorder || inInnerSquare;
      } else {
        // Data pattern based on hash
        const seed = (hash + row * gridSize + col) % 100;
        cells[row][col] = seed > 45;
      }
    }
  }

  const cellSize = size / gridSize;

  return (
    <svg
      width={size}
      height={size}
      viewBox={`0 0 ${size} ${size}`}
      className="rounded-lg"
    >
      <rect width={size} height={size} fill="white" />
      {cells.map((row, rowIndex) =>
        row.map((cell, colIndex) =>
          cell ? (
            <rect
              key={`${rowIndex}-${colIndex}`}
              x={colIndex * cellSize}
              y={rowIndex * cellSize}
              width={cellSize}
              height={cellSize}
              fill="black"
            />
          ) : null
        )
      )}
    </svg>
  );
}
function SalesOrderFormDrawerInner({
  contract,
  open,
  onOpenChange,
  onSave,
  isSaving = false,
}: SalesOrderFormDrawerProps) {
  const dispatch = useDispatch();
  const { t, i18n } = useTranslation();
  const isRTL = useIsRTL();
  const isArabic = i18n.language === 'ar'; // Keep for content selection (Arabic-specific labels)

  // Determine mode: edit (has ID), reorder (has data but no ID), or create new
  const isEdit = !!contract?.id;
  const isReorder = !!contract && !contract.id;
  const lockedFactoryId = String(contract?.factoryId ?? '').trim();
  const originalOrderNumber = isReorder
    ? (contract as any)._originalNumber || contract.orderNumber
    : null;
  const canEditOrderLines = !isEdit;

  const allowedCityIds = useSelector(selectAllowedCityIds);
  const isAdmin = useSelector(selectIsAdmin);
  const selectedTenant = useSelector(selectSelectedTenant);
  const authUser = useSelector(selectAuthUser);
  const [selectedCustomerTenant, setSelectedCustomerTenant] =
    useState<TenantRef | null>(null);
  const [customerPickerOpen, setCustomerPickerOpen] = useState(false);
  const [customerSearch, setCustomerSearch] = useState('');
  const [customerOptions, setCustomerOptions] = useState<
    Array<{
      value: string;
      label: string;
      tenant: TenantRef;
    }>
  >([]);
  const [customersPage, setCustomersPage] = useState(1);
  const [customersHasMore, setCustomersHasMore] = useState(false);
  const [customersLoading, setCustomersLoading] = useState(false);
  const [debouncedCustomerSearch, setDebouncedCustomerSearch] = useState('');
  const customerFetchIdRef = useRef(0);

  const getTenantIdentifier = useCallback((tenant: any): string => {
    return String(
      tenant?.documentId ??
        tenant?.id ??
        tenant?.tenantId ??
        tenant?.customerId ??
        ''
    ).trim();
  }, []);

  const mergedCustomerOptions = useMemo(() => {
    const selectedValue = getTenantIdentifier(selectedCustomerTenant);
    if (!selectedValue || !selectedCustomerTenant?.name) return customerOptions;

    const exists = customerOptions.some(
      (option) => option.value === selectedValue
    );
    if (exists) return customerOptions;

    return [
      {
        value: selectedValue,
        label: selectedCustomerTenant.name,
        tenant: selectedCustomerTenant,
      },
      ...customerOptions,
    ];
  }, [customerOptions, selectedCustomerTenant, getTenantIdentifier]);

  const resolveNonAdminTenant = useCallback(() => {
    const userTenant = authUser?.tenant;
    if (!userTenant) return null;
    return {
      name: userTenant.name,
      code: userTenant.code ?? undefined,
      customerId: getTenantIdentifier(userTenant),
    };
  }, [authUser, getTenantIdentifier]);

  useEffect(() => {
    if (!open) return;
    if (isEdit) return;
    setCustomerSearch('');
    setCustomerPickerOpen(false);
    setDebouncedCustomerSearch('');
    setCustomersPage(1);
    setCustomerOptions([]);
    setCustomersHasMore(false);

    if (isAdmin) {
      setSelectedCustomerTenant(selectedTenant ?? null);
      return;
    }

    setSelectedCustomerTenant(resolveNonAdminTenant());
  }, [open, isEdit, isAdmin, selectedTenant, resolveNonAdminTenant]);

  useEffect(() => {
    if (!open || !isAdmin || isEdit) return;
    const timeoutId = window.setTimeout(() => {
      setDebouncedCustomerSearch(customerSearch.trim());
      setCustomersPage(1);
    }, 300);
    return () => window.clearTimeout(timeoutId);
  }, [open, isAdmin, isEdit, customerSearch]);

  const fetchCustomerOptions = useCallback(
    async (page: number, append: boolean) => {
      if (!open || !isAdmin || isEdit) return;
      const fetchId = ++customerFetchIdRef.current;
      setCustomersLoading(true);
      try {
        const response = await customerAccountsApi.getAccounts({
          page,
          pageSize: 10,
          search: debouncedCustomerSearch || undefined,
        });

        if (fetchId !== customerFetchIdRef.current) return;

        const mapped = response.data
          .map((account) => {
            const tenant = account.tenant;
            const value = getTenantIdentifier(tenant);
            if (!value || !tenant?.name) return null;
            return {
              value,
              label: tenant.name,
              tenant: {
                id: String(tenant.id),
                documentId: String(tenant.documentId || tenant.id),
                code: tenant.code || '',
                name: tenant.name,
                channel: (tenant.channel || 'b2b') as TenantRef['channel'],
              } satisfies TenantRef,
            };
          })
          .filter(
            (
              item
            ): item is { value: string; label: string; tenant: TenantRef } =>
              Boolean(item)
          );

        const deduped = Array.from(
          new Map(mapped.map((item) => [item.value, item])).values()
        );

        setCustomerOptions((prev) => {
          if (!append) return deduped;
          const merged = new Map(prev.map((item) => [item.value, item]));
          for (const item of deduped) merged.set(item.value, item);
          return Array.from(merged.values());
        });

        setCustomersHasMore(page < response.pagination.pageCount);
        setCustomersLoading(false);
      } catch {
        if (fetchId !== customerFetchIdRef.current) return;
        if (!append) setCustomerOptions([]);
        setCustomersHasMore(false);
        setCustomersLoading(false);
      }
    },
    [open, isAdmin, isEdit, debouncedCustomerSearch, getTenantIdentifier]
  );

  useEffect(() => {
    if (!open || !isAdmin || isEdit) return;
    fetchCustomerOptions(customersPage, customersPage > 1);
  }, [open, isAdmin, isEdit, customersPage, fetchCustomerOptions]);

  const customerDisplay = useMemo<{ name: string } | null>(() => {
    const contractTenant = (contract as any)?.tenant;
    if (contractTenant?.name) {
      return { name: contractTenant.name };
    }
    if (isAdmin) {
      if (!selectedCustomerTenant) return null;
      return {
        name: selectedCustomerTenant.name,
      };
    }
    const nonAdminTenant = resolveNonAdminTenant();
    if (!nonAdminTenant?.name) return null;
    return { name: nonAdminTenant.name };
  }, [contract, isAdmin, selectedCustomerTenant, resolveNonAdminTenant]);

  // Get channel from user's tenant
  // Default to B2B behavior unless explicitly ecommerce
  const userChannel = useSelector(selectUserChannel);
  const isB2B = userChannel !== 'ecommerce';

  // Tenant documentId used to scope SubCustomerSelector to the correct tenant
  const formTenantDocumentId: string | null = isAdmin
    ? (selectedCustomerTenant?.documentId ?? null)
    : (authUser?.tenant?.documentId ? String(authUser.tenant.documentId) : null);

  // Per-tenant policy: some customers must pick a sub-customer (backend enforces it too)
  const [adminTenantRequiresSubCustomer, setAdminTenantRequiresSubCustomer] = useState(false);
  useEffect(() => {
    setAdminTenantRequiresSubCustomer(false);
    if (!open || isEdit || !isAdmin || !formTenantDocumentId) return;
    let cancelled = false;
    tenantsApi
      .getById(formTenantDocumentId)
      .then((tenant) => {
        if (!cancelled) setAdminTenantRequiresSubCustomer(Boolean(tenant.requireSubCustomer));
      })
      .catch(() => undefined);
    return () => {
      cancelled = true;
    };
  }, [open, isEdit, isAdmin, formTenantDocumentId]);
  const subCustomerRequired =
    !isEdit &&
    (isAdmin ? adminTenantRequiresSubCustomer : Boolean(authUser?.tenant?.requireSubCustomer));

  // Credit data for B2B customers — uses same SAP balance as finance/payments page
  const sapBalances = useSelector(selectSapBalances);
  const creditLoading = useSelector(selectSapBalancesLoading);
  const sapBalancesError = useSelector(selectSapBalancesError);
  // If the last fetch failed, show 0 so stale data is not misleading
  const availableCredit =
    sapBalancesError || !sapBalances?.contractBalances?.length
      ? 0
      : sapBalances.contractBalances.reduce((s, c) => s + c.totalNetAmount, 0);

  const error = useSelector(selectSalesOrdersError);

  // Show error as toast instead of inline display
  useEffect(() => {
    if (error) {
      toast({
        variant: 'destructive',
        title: t('common.error', 'Error'),
        description: error,
      });
      dispatch(salesOrdersActions.clearError());
    }
  }, [error, dispatch, t]);

  // Clear error when drawer opens/closes
  useEffect(() => {
    if (!open && error) {
      dispatch(salesOrdersActions.clearError());
    }
  }, [open, error, dispatch]);

  // Fetch SAP balances when drawer opens (same source as finance/payments page)
  useEffect(() => {
    if (open) {
      dispatch(financeActions.fetchSapBalancesCached());
    }
  }, [open, dispatch]);

  // Re-fetch fresh balances when the selected customer changes (admin picks a different tenant)
  useEffect(() => {
    if (open && selectedCustomerTenant) {
      dispatch(financeActions.fetchSapBalances());
    }
  }, [selectedCustomerTenant, open, dispatch]);

  // City data for sales-order destination selection (loaded per factory, see effect below)
  const cities = useSelector(selectCities);

  // State declarations (must come before useEffects that use them)
  const [formData, setFormData] = useState({
    paymentMethod: 'credit_limit' as PaymentMethodType,
    cityId: '',
    cityName: '',
    notes: '',
  });

  // Factory selection state (must be declared before deliveryCityOptions useMemo)
  const [availableFactories, setAvailableFactories] = useState<
    FactoryListItem[]
  >([]);
  const [selectedFactoryId, setSelectedFactoryId] = useState<string>('');
  const [factoriesLoading, setFactoriesLoading] = useState(false);
  const [factoryCities, setFactoryCities] = useState<
    Array<{
      id: string;
      documentId: string;
      name: string;
      nameEn: string;
      nameAr?: string;
      countryId?: string;
    }>
  >([]);
  const [citySearch, setCitySearch] = useState('');
  const [cityDropdownOpen, setCityDropdownOpen] = useState(false);
  const [factoryCitiesLoading, setFactoryCitiesLoading] = useState(false);

  const deliveryCityOptions = useMemo(
    () =>
      buildDeliveryCityOptions(
        factoryCities as any,
        allowedCityIds,
        formData.cityId
      ),
    [factoryCities, allowedCityIds, formData.cityId]
  );

  const selectedDeliveryCity = useMemo(
    () =>
      deliveryCityOptions.find((city) =>
        matchesCityOption(city, formData.cityId)
      ) ?? null,
    [deliveryCityOptions, formData.cityId]
  );

  const selectedCityDocumentId =
    selectedDeliveryCity?.documentId ??
    (formData.cityId && !/^\d+$/.test(formData.cityId)
      ? formData.cityId
      : undefined);

  // Bilingual search: match city name against EN, AR, or current locale name
  const filteredDeliveryCityOptions = useMemo(() => {
    if (!citySearch.trim()) return deliveryCityOptions;
    const q = citySearch.trim().toLowerCase();
    return deliveryCityOptions.filter(
      (city) =>
        city.name.toLowerCase().includes(q) ||
        city.nameEn.toLowerCase().includes(q) ||
        (city.nameAr && city.nameAr.toLowerCase().includes(q))
    );
  }, [deliveryCityOptions, citySearch]);

  const [lines, setLines] = useState<OrderLineForm[]>([createEmptyLine()]);
  const [splitEditors, setSplitEditors] = useState<
    Record<string, { expanded: boolean; page: number }>
  >({});
  const [resourceSearch, setResourceSearch] = useState('');
  const [driverSearch, setDriverSearch] = useState('');
  const [vehicleSearch, setVehicleSearch] = useState('');
  const [vibanCopied, setVibanCopied] = useState(false);

  const [resourceOptions, setResourceOptions] = useState<
    Array<{
      id: string;
      documentId?: string;
      name: string;
      driverName?: string;
      powerUnitLabel?: string;
      trailerLabel?: string;
    }>
  >([]);
  const [resourcePagination, setResourcePagination] = useState<any>(null);
  const [resourceDropdownTarget, setResourceDropdownTarget] = useState<{
    lineId: string;
    splitIndex: number | null;
  } | null>(null);
  const [resourceLoading, setResourceLoading] = useState(false);
  const [resourceLoadingMore, setResourceLoadingMore] = useState(false);
  const [resourcePage, setResourcePage] = useState(1);
  const resourceFetchKeyRef = useRef(0);
  const [debouncedResourceSearch, setDebouncedResourceSearch] = useState('');

  const [isCreatingResource, setIsCreatingResource] = useState(false);
  const [showCreateConfirmation, setShowCreateConfirmation] = useState(false);
  const [pendingCreatePayload, setPendingCreatePayload] = useState<any | null>(
    null
  );
  const [pendingCreateSummary, setPendingCreateSummary] =
    useState<CreateConfirmationSummary | null>(null);

  // Sub-customer selection (create-only, optional)
  const [selectedSubCustomer, setSelectedSubCustomer] = useState<SubCustomer | null>(null);
  const [showSubCustomerSelector, setShowSubCustomerSelector] = useState(false);

  useEffect(() => {
    if (!open) return;
    if (formData.cityId) return;
    if (!selectedDeliveryCity?.name) return;
    setFormData((prev) => ({ ...prev, cityName: selectedDeliveryCity.name }));
  }, [open, formData.cityId, selectedDeliveryCity]);

  // Load ALL factories when drawer opens (plant is selected first)
  useEffect(() => {
    if (!open) return;
    let cancelled = false;
    setFactoriesLoading(true);

    factoriesApi
      .getFactories({ status: 'active' })
      .then((factories) => {
        if (cancelled) return;
        setAvailableFactories(factories ?? []);
        if (isEdit && lockedFactoryId) {
          setSelectedFactoryId(lockedFactoryId);
        } else if (!isEdit && factories.length === 1) {
          setSelectedFactoryId(factories[0].documentId || factories[0].id);
        } else if (!isEdit) {
          // Only reset if not already set from contract init
        }
      })
      .catch(() => {
        if (!cancelled) setAvailableFactories([]);
      })
      .finally(() => {
        if (!cancelled) setFactoriesLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [open, i18n.language]);

  // Load cities for the selected factory
  useEffect(() => {
    if (!selectedFactoryId) {
      setFactoryCities([]);
      if (!isEdit) {
        setFormData((prev) => ({ ...prev, cityId: '', cityName: '' }));
      }
      return;
    }

    let cancelled = false;
    setFactoryCitiesLoading(true);

    factoriesApi
      .getFactory(selectedFactoryId)
      .then(async (fullFactory) => {
        if (cancelled) return;

        // Extract EN/AR names from each city's localizations (populated via Strapi i18n)
        // Factory is queried with locale=en, so c.name is always the English name
        const cities = (fullFactory?.cities ?? []).map((c: any) => {
          let nameEn = c.name;
          let nameAr: string | undefined;

          if (c.localizations && Array.isArray(c.localizations)) {
            for (const loc of c.localizations) {
              if (loc.locale === 'en' && loc.name) nameEn = loc.name;
              if (loc.locale === 'ar' && loc.name) nameAr = loc.name;
            }
          }

          const isArabic = i18n.language === 'ar';

          return {
            id: c.id,
            documentId: c.documentId,
            name: isArabic ? (nameAr ?? nameEn) : nameEn,
            nameEn,
            nameAr,
            countryId: c.countryId,
          };
        });

        setFactoryCities(cities);
        // Reset city if current city is no longer served by new factory
        setFormData((prev) => {
          if (!prev.cityId) return prev;
          const stillValid = cities.some(
            (c: any) => c.id === prev.cityId || c.documentId === prev.cityId
          );
          if (!stillValid) return { ...prev, cityId: '', cityName: '' };
          return prev;
        });
      })
      .catch(() => {
        if (!cancelled) setFactoryCities([]);
      })
      .finally(() => {
        if (!cancelled) setFactoryCitiesLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [selectedFactoryId, i18n.language]);

  const [skuOptions, setSkuOptions] = useState<SkuOption[]>([]);
  const [skuLoading, setSkuLoading] = useState(false);
  const [categoryOptions, setCategoryOptions] = useState<Category[]>([]);
  const [categoriesLoading, setCategoriesLoading] = useState(false);

  const initKey =
    contract?.id ||
    (contract as any)?._originalNumber ||
    (contract ? 'contract' : 'new');
  const initKeyRef = useRef<string | null>(null);
  const skuPrefillKeyRef = useRef<string | null>(null);

  const filteredResources = resourceOptions;

  useEffect(() => {
    if (!open || !resourceDropdownTarget) {
      setDebouncedResourceSearch('');
      return;
    }
    const timer = setTimeout(() => {
      setDebouncedResourceSearch(resourceSearch.trim());
    }, 500);
    return () => clearTimeout(timer);
  }, [resourceSearch, resourceDropdownTarget, open]);

  useEffect(() => {
    if (!open) return;
    if (debouncedResourceSearch && debouncedResourceSearch.length < 2) return;
    setResourcePage(1);
    setResourcePagination(null);
    setResourceOptions([]);
  }, [debouncedResourceSearch, resourceDropdownTarget, open]);

  useEffect(() => {
    if (!open) return;
    // Wait for factory selection before fetching products
    if (!selectedFactoryId && availableFactories.length > 0) return;
    let cancelled = false;
    setSkuLoading(true);

    // Use factory ID for product availability (NEW - primary method)
    skusApi
      .getProductsWithPricing(
        selectedFactoryId
          ? { factoryId: selectedFactoryId, locale: i18n.language }
          : { locale: i18n.language }
      )
      .then((products) => {
        if (cancelled) return;

        console.log('Raw products from API:', products);

        const options: SkuOption[] = products
          .map((product) => {
            const categories = skusApi.extractCategories(product);
            const productCode = categories?.[0]?.code ?? '';
            const pricedVariants = skusApi.extractPricedVariants(product);
            const productImageUrl = getProductVariantImageUrl(product);

            // Debug: log the product structure
            console.log('Processing product:', {
              id: product.id,
              name: product.name,
              imageUrl: productImageUrl,
              categories: product.categories,
              extractedCategories: categories,
              variantsCount: product.variants?.length,
              pricedVariantsCount: pricedVariants.length,
            });

            const variants: VariantOption[] = pricedVariants.map((v) => {
              const documentId =
                typeof (v as any)?.documentId === 'string'
                  ? String((v as any).documentId).trim()
                  : '';
              const strapiId =
                (v as any)?.id != null ? String((v as any).id).trim() : '';
              const id = documentId || strapiId;

              return {
                id,
                documentId: documentId || undefined,
                strapiId: strapiId || undefined,
                name: String(v.name ?? v.skuCode ?? v.id ?? ''),
                skuCode: v.skuCode ?? null,
                variantCode:
                  typeof (v as any)?.variantCode === 'string'
                    ? String((v as any).variantCode)
                    : null,
                packaging:
                  (v as any)?.packaging === 'bulk' ||
                  (v as any)?.packaging === 'bags'
                    ? (v as any).packaging
                    : (v as any)?.packagingType === 'bulk' ||
                      (v as any)?.packagingType === 'bags'
                    ? (v as any).packagingType
                    : null,
                weightKg: Number.isFinite(Number((v as any)?.weight))
                  ? Number((v as any).weight)
                  : null,
                unitPrice: v.unitPrice ?? null,
                minQuantity: v.minQuantity ?? null,
                maxQuantity: v.maxQuantity ?? null,
                minOrderQuantity: (v as any)?.minOrderQuantity ?? null,
                maxOrderQuantity: (v as any)?.maxOrderQuantity ?? null,
                isAvailable: (v as any)?.isAvailable ?? null,
                imageUrl:
                  getPrimaryImageUrl((v as { images?: unknown }).images) ??
                  productImageUrl,
              };
            });
            return {
              id: String(product.id ?? ''),
              name: String(product.name ?? product.code ?? product.id ?? ''),
              productCode,
              variants,
              categoryId:
                (product as any)?.categories?.[0]?.id ??
                categories?.[0]?.id ??
                undefined,
            };
          })
          // Filter out products without variants
          .filter((sku) => {
            const hasVariants = sku.variants.length > 0;
            if (!hasVariants) {
              console.log('Filtering out product without variants:', sku.name);
            }
            return hasVariants;
          });

        console.log('Final SKU options:', options);
        console.log('SKU options count:', options.length);

        setSkuOptions(options);
      })
      .catch((error) => {
        console.error('Failed to fetch products with pricing:', error);
        if (!cancelled) setSkuOptions([]);
      })
      .finally(() => {
        if (!cancelled) setSkuLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [open, selectedFactoryId, availableFactories.length, i18n.language]);

  // Live wait per cement type at the chosen plant ("if a truck arrives now").
  const [waitByVariant, setWaitByVariant] = useState<Record<string, number | null>>({});
  useEffect(() => {
    if (!open || !selectedFactoryId) {
      setWaitByVariant({});
      return;
    }
    let cancelled = false;
    salesOrdersApi.getWaitTimes(selectedFactoryId).then((waits) => {
      if (!cancelled) setWaitByVariant(waits);
    });
    return () => {
      cancelled = true;
    };
  }, [open, selectedFactoryId]);

  // Fetch categories
  useEffect(() => {
    if (!open) return;
    let cancelled = false;
    setCategoriesLoading(true);

    productsApi.categories
      .getList()
      .then((categories) => {
        if (cancelled) return;
        // Filter only active categories
        const activeCategories = categories.filter((c) => c.isActive);
        setCategoryOptions(activeCategories);
      })
      .catch((error) => {
        console.error('Failed to fetch categories:', error);
        if (!cancelled) setCategoryOptions([]);
      })
      .finally(() => {
        if (!cancelled) setCategoriesLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [open]);

  useEffect(() => {
    if (!open) {
      // initKeyRef.current = null
      return;
    }

    if (initKeyRef.current === initKey) return;
    initKeyRef.current = initKey;

    if (contract) {
      setFormData({
        paymentMethod: contract.paymentMethod || 'credit_limit',
        cityId: contract.cityId || '',
        cityName: contract.cityName || '',
        notes: contract.notes || '',
      });
      setSelectedFactoryId(String(contract.factoryId ?? '').trim());
      const sourceLines =
        Array.isArray((contract as any).lines) &&
        (contract as any).lines.length > 0
          ? (contract as any).lines
          : (contract as any).products || [];

      setLines(
        sourceLines.map((p: any, idx: number) => {
          // Check if the source data had 'exworks' as delivery option (legacy) or has isExworks flag
          const rawDeliveryOption = p?.deliveryOption ?? p?.fulfillment;
          const isExworksFromLegacy =
            typeof rawDeliveryOption === 'string' &&
            rawDeliveryOption.toLowerCase() === 'exworks';
          // For reorder mode, default to exworks=true so the form is immediately valid
          // (user can change fulfillment options before placing the order)
          const isExworks = isReorder
            ? true
            : p?.isExworks === true || isExworksFromLegacy;

          const baseLine: OrderLineForm = {
            id: String(
              p?.strapiDocumentId ??
                p?.documentId ??
                p?.strapiId ??
                p?.salesOrderLineDocumentId ??
                p?.salesOrderLineId ??
                idx
            ),
            // For reorder, don't carry over line IDs since this is a new order
            salesOrderLineId: isReorder
              ? undefined
              : p?.strapiId != null
              ? String(p.strapiId)
              : p?.salesOrderLineId != null
              ? String(p.salesOrderLineId)
              : undefined,
            salesOrderLineDocumentId: isReorder
              ? undefined
              : p?.strapiDocumentId ??
                p?.salesOrderLineDocumentId ??
                p?.documentId,
            skuId: String(p?.skuId || p?.productId || ''),
            variantId: String(p?.variantId || ''),
            productCode: String(p?.productCode || ''),
            packaging: normalizePackaging(p?.packaging),
            bulkOption: 'other' as BulkQuantityOption,
            quantity: String(
              p?.allocatedQuantity?.value ?? p?.quantity?.value ?? ''
            ),
            splitBy: String(
              p?.splitBy ??
                p?.allocatedQuantity?.value ??
                p?.quantity?.value ??
                ''
            ),
            numberOfOrders: (() => {
              const qty = Number.parseFloat(
                String(p?.allocatedQuantity?.value ?? p?.quantity?.value ?? '')
              );
              const sb = Number.parseFloat(
                String(
                  p?.splitBy ??
                    p?.allocatedQuantity?.value ??
                    p?.quantity?.value ??
                    ''
                )
              );
              if (qty > 0 && sb > 0 && Number.isFinite(qty / sb))
                return String(Math.round(qty / sb));
              return '1';
            })(),
            unitPrice: String(p?.unitPrice?.amount ?? ''),
            deliveryOption: isReorder
              ? 'assign'
              : (normalizeDeliveryOptionValue(
                  p?.deliveryOption ?? p?.fulfillment
                ) as DeliveryOptionType),
            isExworks,
            assignee: isReorder ? '' : p?.assignee || '',
            resourceId: isReorder
              ? undefined
              : p?.resourceId ?? p?.metadata?.resourceId ?? undefined,
            resourceDocumentId: isReorder
              ? undefined
              : p?.resourceDocumentId ??
                p?.metadata?.resourceDocumentId ??
                undefined,
            deliveryOptionSplits: [],
          };

          if (!isReorder) {
            const rawSplits = Array.isArray(p?.deliveryOptionSplits)
              ? p.deliveryOptionSplits
              : Array.isArray(p?.fulfillmentSplits)
              ? p.fulfillmentSplits
              : [];
            const normalizedSplits = rawSplits.map((split: any) => ({
              deliveryOption: normalizeDeliveryOptionValue(
                split?.deliveryOption ||
                  split?.fulfillment ||
                  baseLine.deliveryOption
              ),
              isExworks:
                split?.isExworks === true ||
                (typeof split?.deliveryOption === 'string' &&
                  split.deliveryOption.toLowerCase() === 'exworks'),
              assignee:
                typeof split?.assignee === 'string' ? split.assignee : '',
              resourceId:
                split?.resourceId != null
                  ? String(split.resourceId)
                  : undefined,
              resourceDocumentId:
                typeof split?.resourceDocumentId === 'string'
                  ? split.resourceDocumentId
                  : undefined,
            }));

            const splitPlan = getSplitPlanForLine(baseLine, skuOptions);
            baseLine.deliveryOptionSplits =
              splitPlan.count > 0
                ? normalizedSplits.slice(0, splitPlan.count)
                : [];
          }

          return baseLine;
        })
      );
      return;
    }

    setFormData({
      paymentMethod: 'credit_limit',
      cityId: '',
      cityName: '',
      notes: '',
    });
    setSelectedFactoryId('');
    setLines([createEmptyLine()]);
    setSelectedSubCustomer(null);
    setShowSubCustomerSelector(false);
  }, [open, contract, initKey, skuOptions]);

  useEffect(() => {
    if (!open) {
      // skuPrefillKeyRef.current = null
      return;
    }
    if (skuOptions.length === 0) return;

    if (skuPrefillKeyRef.current === initKey) return;
    skuPrefillKeyRef.current = initKey;

    const normalize = (value: string) => value.trim().toLowerCase();

    setLines((prev) => {
      let changed = false;

      const next = prev.map((line) => {
        const candidateSkuId = String(line.skuId || '').trim();
        const candidateCode = String(line.productCode || '').trim();
        const candidateVariantId = String(line.variantId || '').trim();

        const skuByVariantId = candidateVariantId
          ? skuOptions.find((sku) =>
              sku.variants.some((v) => matchesVariantId(v, candidateVariantId))
            )
          : undefined;

        const skuByLegacyVariantId = candidateSkuId
          ? skuOptions.find((sku) =>
              sku.variants.some((v) => matchesVariantId(v, candidateSkuId))
            )
          : undefined;

        const skuByProductCode = candidateCode
          ? skuOptions.find(
              (sku) => normalize(sku.productCode) === normalize(candidateCode)
            )
          : undefined;

        const skuById = candidateSkuId
          ? skuOptions.find((sku) => sku.id === candidateSkuId)
          : undefined;

        const chosenSku =
          skuByProductCode &&
          skuByVariantId &&
          skuByProductCode.id === skuByVariantId.id
            ? skuByProductCode
            : skuByProductCode ||
              skuByVariantId ||
              skuByLegacyVariantId ||
              skuById;

        if (!chosenSku) return line;

        const normalizedPackaging = normalizePackaging(line.packaging);

        const isSkuMismatch = line.skuId !== chosenSku.id;
        const isCodeMismatch =
          candidateCode &&
          normalize(chosenSku.productCode) !== normalize(candidateCode);

        const variantFromLegacyVariantId = skuByLegacyVariantId
          ? chosenSku.variants.find((v) => matchesVariantId(v, candidateSkuId))
          : undefined;

        const variantFromExistingVariantId = candidateVariantId
          ? chosenSku.variants.find((v) =>
              matchesVariantId(v, candidateVariantId)
            )
          : undefined;

        const variantByPackaging = pickPreferredVariant(
          chosenSku.variants,
          normalizedPackaging
        );

        const chosenVariant =
          variantFromExistingVariantId ||
          variantFromLegacyVariantId ||
          variantByPackaging;

        const nextSkuId = chosenSku.id;
        const nextVariantId = chosenVariant ? chosenVariant.id : line.variantId;
        const nextPackaging = chosenVariant
          ? inferPackagingFromVariant(chosenVariant)
          : normalizedPackaging;
        const nextProductCode = chosenSku.productCode || line.productCode;
        // Get current price from pricing API (only if line doesn't have a price already)
        const nextUnitPrice =
          !line.unitPrice && chosenVariant?.unitPrice != null
            ? String(chosenVariant.unitPrice)
            : line.unitPrice;

        const needsUpdate =
          isSkuMismatch ||
          !line.skuId ||
          isCodeMismatch ||
          !line.variantId ||
          line.variantId !== nextVariantId ||
          line.packaging !== nextPackaging ||
          line.productCode !== nextProductCode ||
          (!line.unitPrice && nextUnitPrice);

        if (!needsUpdate) return line;

        changed = true;
        return {
          ...line,
          skuId: nextSkuId,
          variantId: nextVariantId,
          productCode: nextProductCode,
          packaging: nextPackaging,
          unitPrice: nextUnitPrice,
        };
      });

      return changed ? next : prev;
    });
  }, [open, skuOptions, initKey]);

  const addLine = () => {
    if (!canEditOrderLines) return;
    setLines((prev) => [...prev, createEmptyLine()]);
  };

  const removeLine = (id: string) => {
    if (!canEditOrderLines) return;
    if (lines.length > 1) setLines((prev) => prev.filter((l) => l.id !== id));
  };

  const updateLine = (id: string, updates: Partial<OrderLineForm>) => {
    if (!canEditOrderLines) return;
    setLines((prev) =>
      prev.map((line) => {
        if (line.id !== id) return line;
        const updated = { ...line, ...updates };
        const packagingOverride = isPackagingType(updates.packaging)
          ? updates.packaging
          : null;

        // When SKU changes, derive product type + default variant + price
        if (updates.skuId) {
          const sku = skuOptions.find((s) => s.id === updates.skuId);
          if (sku) {
            updated.productCode = sku.productCode;
            const preferredPackaging =
              packagingOverride ??
              (isPackagingType(updated.packaging) ? updated.packaging : null);
            const requestedVariant = updates.variantId
              ? sku.variants.find((v) =>
                  matchesVariantId(v, String(updates.variantId))
                )
              : null;
            const defaultVariant =
              requestedVariant ??
              (preferredPackaging
                ? pickDefaultVariantByPackaging(
                    sku.variants,
                    preferredPackaging
                  )
                : pickFirstAvailableVariant(sku.variants));
            if (defaultVariant) {
              updated.variantId = defaultVariant.id;
              updated.packaging =
                packagingOverride ?? inferPackagingFromVariant(defaultVariant);
              if (
                isPackagingType(updated.packaging) &&
                updated.packaging !== line.packaging
              ) {
                updated.splitBy = getDefaultSplitByValue(updated.packaging);
              }
              // Auto-populate unit price from variant
              updated.unitPrice =
                defaultVariant.unitPrice != null
                  ? String(defaultVariant.unitPrice)
                  : '';
            } else {
              updated.variantId = '';
              updated.unitPrice = '';
              if (packagingOverride) {
                updated.skuId = '';
                updated.productCode = '';
              }
            }
          }
        }

        // When variant changes, derive packaging + price
        if (updates.variantId) {
          const sku = skuOptions.find(
            (s) => s.id === (updated.skuId || line.skuId)
          );
          const variant =
            sku?.variants.find((v) =>
              matchesVariantId(v, updates.variantId as string)
            ) || null;
          if (variant) {
            updated.variantId = variant.id;
            updated.packaging = inferPackagingFromVariant(variant);
            if (
              isPackagingType(updated.packaging) &&
              updated.packaging !== line.packaging
            ) {
              updated.splitBy = getDefaultSplitByValue(updated.packaging);
            }
            // Auto-populate unit price from variant
            updated.unitPrice =
              variant.unitPrice != null ? String(variant.unitPrice) : '';
          } else {
            updated.variantId = '';
            updated.unitPrice = '';
          }
        }

        if (packagingOverride) {
          const packagingChanged = packagingOverride !== line.packaging;
          updated.packaging = packagingOverride;
          if (packagingChanged) {
            updated.splitBy = getDefaultSplitByValue(packagingOverride);
          }

          const sku = skuOptions.find(
            (s) => s.id === (updated.skuId || line.skuId)
          );
          if (sku) {
            const matchingVariant = pickDefaultVariantByPackaging(
              sku.variants,
              packagingOverride
            );
            if (matchingVariant) {
              updated.variantId = matchingVariant.id;
              updated.productCode = sku.productCode;
              updated.unitPrice =
                matchingVariant.unitPrice != null
                  ? String(matchingVariant.unitPrice)
                  : '';
            } else {
              updated.skuId = '';
              updated.variantId = '';
              updated.productCode = '';
              updated.unitPrice = '';
            }
          } else {
            updated.variantId = '';
            updated.productCode = '';
            updated.unitPrice = '';
          }
        }

        // Quantity is entered directly by the user; no auto-computation needed.

        // EXW is automatic: selected when no resource is assigned.
        const hasSelectedResource =
          toNonEmptyString(updated.resourceId) != null ||
          toNonEmptyString(updated.resourceDocumentId) != null;
        updated.isExworks = !hasSelectedResource;

        const hasDeliveryOptionUpdate =
          typeof updates.deliveryOption === 'string';

        // Note: We no longer clear assignee data when deliveryOption changes
        // since assignment is always available regardless of delivery option

        // When the default delivery option changes, only update splits that were
        // previously following the old default. Explicit overrides keep their value.
        if (hasDeliveryOptionUpdate) {
          const oldDefault = line.deliveryOption || 'assign';
          const newDefault = updates.deliveryOption as DeliveryOptionType;
          const prevSplits = Array.isArray(line.deliveryOptionSplits)
            ? [...line.deliveryOptionSplits]
            : [];
          const nextSplits: DeliveryOptionSplitForm[] = [];

          for (let index = 0; index < prevSplits.length; index += 1) {
            const split = prevSplits[index];
            if (!split) {
              // No explicit override → this split always follows the default.
              nextSplits[index] = undefined as any;
              continue;
            }

            const splitOption = split.deliveryOption || 'assign';
            const splitAssignee = toNonEmptyString(split.assignee);
            const defaultAssignee = toNonEmptyString(line.assignee);
            const splitResourceId = toNonEmptyString(split.resourceId);
            const splitResourceDocumentId = toNonEmptyString(
              split.resourceDocumentId
            );
            const defaultResourceId = toNonEmptyString(line.resourceId);
            const defaultResourceDocumentId = toNonEmptyString(
              line.resourceDocumentId
            );

            const sameAsOldDefault =
              splitOption === oldDefault &&
              (splitOption !== 'assign' ||
                ((splitAssignee ?? '') === (defaultAssignee ?? '') &&
                  (splitResourceId ?? '') === (defaultResourceId ?? '') &&
                  (splitResourceDocumentId ?? '') ===
                    (defaultResourceDocumentId ?? '')));

            if (sameAsOldDefault) {
              // This split was effectively "use default" → clear override so it now
              // follows the new default option.
              nextSplits[index] = undefined as any;
            } else {
              // Explicit override different from old default → keep as-is.
              nextSplits[index] = split;
            }
          }

          updated.deliveryOptionSplits = pruneDeliveryOptionSplitOverrides({
            ...updated,
            deliveryOptionSplits: nextSplits,
          });
        } else {
          // No change to default option: just keep overrides minimal relative to current default.
          updated.deliveryOptionSplits =
            pruneDeliveryOptionSplitOverrides(updated);
        }

        return updated;
      })
    );
  };

  const updateSplit = (
    lineId: string,
    splitIndex: number,
    updates: Partial<DeliveryOptionSplitForm>
  ) => {
    if (!canEditOrderLines) return;
    setLines((prev) =>
      prev.map((line) => {
        if (line.id !== lineId) return line;
        if (splitIndex < 0) return line;
        const nextSplits = Array.isArray(line.deliveryOptionSplits)
          ? [...line.deliveryOptionSplits]
          : [];

        const current =
          nextSplits[splitIndex] ?? buildDefaultDeliveryOptionSplit(line);
        const updatedSplit = { ...current, ...updates };

        // EXW is automatic per split: selected when no resource is assigned.
        const hasSelectedSplitResource =
          toNonEmptyString(updatedSplit.resourceId) != null ||
          toNonEmptyString(updatedSplit.resourceDocumentId) != null;
        updatedSplit.isExworks = !hasSelectedSplitResource;

        // Note: We no longer clear assignee data when deliveryOption changes
        // since assignment is always available regardless of delivery option

        nextSplits[splitIndex] = isRedundantDeliveryOptionSplit(
          line,
          updatedSplit
        )
          ? (undefined as any)
          : updatedSplit;
        return {
          ...line,
          deliveryOptionSplits: pruneDeliveryOptionSplitOverrides({
            ...line,
            deliveryOptionSplits: nextSplits,
          }),
        };
      })
    );
  };

  const getSplitEditorState = (lineId: string, splitCount: number) => {
    const state = splitEditors[lineId];
    const expanded = state?.expanded ?? false;
    const totalPages =
      splitCount > 0 ? Math.ceil(splitCount / SPLIT_EDITOR_PAGE_SIZE) : 0;
    const maxPageIndex = Math.max(0, totalPages - 1);
    const page = Math.min(state?.page ?? 0, maxPageIndex);
    return { expanded, page, totalPages };
  };

  const toggleSplitEditor = (lineId: string, expanded: boolean) => {
    setSplitEditors((prev) => ({
      ...prev,
      [lineId]: {
        expanded,
        page: prev[lineId]?.page ?? 0,
      },
    }));
    setResourceDropdownTarget(null);
  };

  const setSplitEditorPage = (lineId: string, page: number) => {
    setSplitEditors((prev) => ({
      ...prev,
      [lineId]: {
        expanded: true,
        page,
      },
    }));
    setResourceDropdownTarget(null);
  };

  useEffect(() => {
    if (!open) {
      setResourceDropdownTarget(null);
      setResourceOptions([]);
      return;
    }
    if (!resourceDropdownTarget) return;

    const activeLine = lines.find(
      (l) => l.id === resourceDropdownTarget.lineId
    );
    if (!activeLine) {
      setResourceDropdownTarget(null);
      setResourceOptions([]);
      return;
    }

    if (!canSelectResourceForLine(activeLine)) {
      setResourceDropdownTarget(null);
      setResourceOptions([]);
      return;
    }

    const splitIndex = resourceDropdownTarget.splitIndex;
    const splitPlan = getSplitPlanForLine(activeLine, skuOptions);
    const splitCount = splitPlan.count;

    if (
      splitIndex != null &&
      (splitCount <= 0 || splitIndex < 0 || splitIndex >= splitCount)
    ) {
      setResourceDropdownTarget(null);
      setResourceOptions([]);
      return;
    }

    const activeSplit =
      splitIndex != null ? getSplitEntry(activeLine, splitIndex) : null;
    const assignee = activeSplit?.assignee ?? activeLine.assignee;

    // Resource selection is always available now (not conditional on deliveryOption)

    const requiredVehicleType = getRequiredVehicleTypeForPackaging(
      activeLine.packaging
    );
    if (!requiredVehicleType) {
      setResourceDropdownTarget(null);
      setResourceOptions([]);
      return;
    }

    const hasSelectedResource = Boolean(
      activeSplit
        ? activeSplit.resourceId || activeSplit.resourceDocumentId
        : activeLine.resourceId || activeLine.resourceDocumentId
    );

    const searchTerm = debouncedResourceSearch.trim();
    if (searchTerm && searchTerm.length < 2) {
      setResourceLoading(false);
      setResourceLoadingMore(false);
      return;
    }
    // If a resource is already selected, don't filter by its name; otherwise it's hard to change selection.
    const fallbackQuery = hasSelectedResource
      ? ''
      : String(assignee ?? '').trim();
    const query = searchTerm || fallbackQuery;

    const fetchKey = ++resourceFetchKeyRef.current;
    if (resourcePage === 1) {
      setResourceLoading(true);
    } else {
      setResourceLoadingMore(true);
    }

    const filter: any = {
      status: 'active',
      powerUnitVehicleType: requiredVehicleType,
      page: resourcePage,
      pageSize: 10,
    };
    if (query) filter.search = query;

    resourcesApi
      .getResourcesPaginated(filter)
      .then(({ data, pagination }) => {
        if (resourceFetchKeyRef.current !== fetchKey) return;
        const mapped = (data || []).map(mapResourceToOption).filter(Boolean);
        setResourceOptions((prev) =>
          resourcePage === 1 ? mapped : [...prev, ...mapped]
        );
        setResourcePagination(pagination);
      })
      .catch(() => {
        if (resourceFetchKeyRef.current !== fetchKey) return;
        setResourceOptions([]);
        setResourcePagination(null);
      })
      .finally(() => {
        if (resourceFetchKeyRef.current !== fetchKey) return;
        setResourceLoading(false);
        setResourceLoadingMore(false);
      });
  }, [
    open,
    resourceDropdownTarget,
    lines,
    debouncedResourceSearch,
    resourcePage,
  ]);

  const resourceHasMore = Boolean(
    resourcePagination && resourcePagination.page < resourcePagination.pageCount
  );

  const handleLoadMoreResources = () => {
    if (resourceLoadingMore || !resourceHasMore) return;
    setResourcePage((prev) => prev + 1);
  };

  const cancelResourceForm = () => {
    setShowResourceForm(false);
  };

  const handleCitySelection = (cityRelationId: string) => {
    const cityOption = deliveryCityOptions.find((city) =>
      matchesCityOption(city, cityRelationId)
    );
    setFormData((prev) => {
      return {
        ...prev,
        cityId: cityRelationId,
        cityName: cityOption?.name || prev.cityName,
      };
    });
    setCitySearch('');
    setCityDropdownOpen(false);
  };

  const copyViban = () => {
    navigator.clipboard.writeText(MOCK_VIBAN.replace(/\s/g, ''));
    setVibanCopied(true);
    setTimeout(() => setVibanCopied(false), 2000);
  };

  const calculateLineTotal = (line: OrderLineForm) =>
    (parseInt(line.quantity) || 0) * (parseFloat(line.unitPrice) || 0);
  const calculateTotal = () =>
    lines.reduce((sum, l) => sum + calculateLineTotal(l), 0);

  const summary = {
    totalLines: lines.length,
    totalQtyBags: lines
      .filter((l) => l.packaging === 'bags')
      .reduce((sum, l) => sum + (parseInt(l.quantity) || 0), 0),
    totalQtyTons: lines
      .filter((l) => l.packaging === 'bulk')
      .reduce((sum, l) => sum + (parseInt(l.quantity) || 0), 0),
    byProduct: Object.entries(
      lines.reduce<
        Record<
          string,
          {
            name: string;
            packaging: PackagingType;
            qty: number;
            generatedQty: number;
            generatedOrders: number;
            droppedQty: number;
            droppedOrders: number;
            total: number;
            unit: string;
            tonsEquivalent: number;
          }
        >
      >((acc, l) => {
        const sku = skuOptions.find((s) => s.id === l.skuId);
        const variant = sku?.variants.find((v) =>
          matchesVariantId(v, l.variantId)
        );
        const baseName =
          (sku?.name || l.productCode || sku?.productCode || '').trim() ||
          t('products.item');
        const key = `${sku?.id || baseName}__${l.packaging}`;
        acc[key] ||= {
          name: baseName,
          packaging: l.packaging,
          qty: 0,
          generatedQty: 0,
          generatedOrders: 0,
          droppedQty: 0,
          droppedOrders: 0,
          total: 0,
          unit:
            l.packaging === 'bulk' ? t('products.tons') : t('products.bags'),
          tonsEquivalent: 0,
        };
        const splitPlan = getSplitPlanForLine(l, skuOptions);
        acc[key].qty += parseInt(l.quantity) || 0;
        acc[key].generatedQty += splitPlan.generatedTotal;
        acc[key].generatedOrders += splitPlan.count;
        acc[key].droppedQty += splitPlan.droppedQuantity;
        acc[key].droppedOrders += splitPlan.droppedCount;
        acc[key].total += calculateLineTotal(l);
        acc[key].unit =
          l.packaging === 'bulk' ? t('products.tons') : t('products.bags');
        if (l.packaging === 'bags') {
          acc[key].tonsEquivalent += calculateBagsTonsEquivalent(
            l.quantity,
            variant?.weightKg
          );
        }
        return acc;
      }, {})
    )
      .map(([key, v]) => {
        const packagingLabel =
          v.packaging === 'bulk' ? t('products.tons') : t('products.bags');
        return {
          code: key,
          name: v.name,
          packaging: v.packaging,
          color: getDotColor(`${v.name}-${v.packaging}`),
          qty: v.qty,
          generatedQty: v.generatedQty,
          generatedOrders: v.generatedOrders,
          droppedQty: v.droppedQty,
          droppedOrders: v.droppedOrders,
          total: v.total,
          unit: packagingLabel,
          tonsEquivalent: v.tonsEquivalent,
        };
      })
      .filter((p) => p.qty > 0),
    byFulfillment: lines.reduce(
      (acc, line) => {
        const splitPlan = getSplitPlanForLine(line, skuOptions);
        const splitCount = splitPlan.count;
        if (splitCount <= 0) return acc;

        // Count exworks based on the isExworks flag
        if (line.isExworks) acc.exworks += splitCount;

        const defaultDeliveryOption = line.deliveryOption || 'assign';
        if (defaultDeliveryOption === 'assign') acc.assign += splitCount;

        const overrides = Array.isArray(line.deliveryOptionSplits)
          ? line.deliveryOptionSplits
          : [];
        overrides.forEach((split, index) => {
          if (!split || index >= splitCount) return;
          const overrideDeliveryOption =
            split.deliveryOption || defaultDeliveryOption;
          if (overrideDeliveryOption === defaultDeliveryOption) return;

          if (defaultDeliveryOption === 'assign')
            acc.assign = Math.max(0, acc.assign - 1);

          if (overrideDeliveryOption === 'assign') acc.assign += 1;
        });
        return acc;
      },
      { assign: 0, exworks: 0 }
    ),
    grandTotal: calculateTotal(),
  };

  const isLineValid = (line: OrderLineForm): boolean => {
    const quantityValue = Number.parseFloat(line.quantity);
    const splitByValue = Number.parseFloat(line.splitBy);
    const splitPlan = getSplitPlanForLine(line, skuOptions);
    const splitCount = splitPlan.count;
    // Each split must be fulfilled either by assigned resource or EXW.
    const hasFulfillmentPlan = Array.from(
      { length: Math.max(0, splitCount) },
      (_, index) => {
        const effective = getSplitEntry(line, index);
        return (
          effective.isExworks === true || hasAssignTarget(effective as any)
        );
      }
    ).every(Boolean);
    const splitByValid = Number.isFinite(splitByValue) && splitByValue > 0;

    // Check MOQ (minQuantity/maxQuantity)
    const sku = skuOptions.find((s) => s.id === line.skuId);
    const variant = sku?.variants.find((v) =>
      matchesVariantId(v, line.variantId)
    );
    const minQty = variant?.minQuantity ?? 1;
    const maxQty = variant?.maxQuantity;
    const meetsMinQty = quantityValue >= minQty;
    const meetsMaxQty = !maxQty || quantityValue <= maxQty;
    const isVariantSupported = variant?.isAvailable !== false;
    const hasPackaging = isPackagingType(line.packaging);

    return (
      !!line.skuId &&
      !!line.variantId &&
      hasPackaging &&
      Number.isFinite(quantityValue) &&
      quantityValue > 0 &&
      splitCount > 0 &&
      meetsMinQty &&
      meetsMaxQty &&
      isVariantSupported &&
      splitByValid &&
      hasFulfillmentPlan
    );
  };

  // Factory is always required (selected first, before city)
  const isFactoryValid = isEdit || Boolean(selectedFactoryId);

  const isValid = Boolean(
    formData.paymentMethod &&
      formData.cityId &&
      isFactoryValid &&
      (!subCustomerRequired || selectedSubCustomer) &&
      (canEditOrderLines ? lines.every(isLineValid) : true)
  );

  const handleSubmit = () => {
    if (!isValid || isSaving || (!isEdit && isAdmin && !selectedCustomerTenant))
      return;
    const orderNumber =
      isEdit && contract?.orderNumber ? contract.orderNumber : undefined;
    const payload: any = {
      ...(orderNumber ? { salesOrderNumber: orderNumber } : {}),
      customerId:
        getTenantIdentifier(selectedCustomerTenant) ||
        getTenantIdentifier(authUser?.tenant) ||
        'unknown',
      name: orderNumber
        ? { en: orderNumber, ar: orderNumber }
        : { en: '', ar: '' },
      // B2B always uses credit_limit, ecommerce uses selected method
      paymentMethod: isB2B ? 'credit_limit' : formData.paymentMethod,
      cityId: formData.cityId,
      cityName: formData.cityName || selectedDeliveryCity?.name,
      factoryId: selectedFactoryId || undefined,
      notes: formData.notes || undefined,
      channel: userChannel || 'ecommerce',
      // Sub-customer is applied to all orders created from this sales order (create-only)
      ...(!isEdit && selectedSubCustomer ? { subCustomerDocumentId: selectedSubCustomer.documentId } : {}),
    };

    if (canEditOrderLines) {
      payload.lines = lines.map((l) => {
        const sku = skuOptions.find((s) => s.id === l.skuId);
        const variant = sku?.variants.find((v) =>
          matchesVariantId(v, l.variantId)
        );
        const splitBy = Number.parseFloat(l.splitBy);
        const unitPriceAmount = Number.parseFloat(l.unitPrice);
        const safeUnitPriceAmount = Number.isFinite(unitPriceAmount)
          ? unitPriceAmount
          : 0;
        const splitPlan = getSplitPlanForLine(l, skuOptions);
        const defaultDeliveryOption = l.deliveryOption || 'assign';
        const deliveryOptionSplits = buildDeliveryOptionSplitsPayload(
          l,
          splitPlan.count
        );
        const defaultAssignee = toNonEmptyString(l.assignee) ?? undefined;
        const defaultResourceId = toNonEmptyString(l.resourceId) ?? undefined;
        const defaultResourceDocumentId =
          toNonEmptyString(l.resourceDocumentId) ?? undefined;
        const salesOrderLineRef = l.salesOrderLineDocumentId
          ? { documentId: l.salesOrderLineDocumentId }
          : l.salesOrderLineId
          ? { id: l.salesOrderLineId }
          : {};
        const requestedQuantity = Number.parseFloat(l.quantity);
        const submittedQuantity =
          splitPlan.generatedTotal > 0
            ? splitPlan.generatedTotal
            : roundQuantity(requestedQuantity);
        const splitAdjustmentMetadata =
          splitPlan.droppedQuantity > 0
            ? {
                requestedQuantity: roundQuantity(requestedQuantity),
                submittedQuantity,
                droppedQuantity: splitPlan.droppedQuantity,
                droppedOrderCount: splitPlan.droppedCount,
                orderQuantityLimits: {
                  minOrderQuantity: splitPlan.minOrderQuantity,
                  maxOrderQuantity: splitPlan.maxOrderQuantity,
                },
              }
            : undefined;
        return {
          ...salesOrderLineRef,
          productId: l.skuId,
          productCode: l.productCode || sku?.productCode || '',
          product: sku?.name
            ? { en: sku.name, ar: sku.name }
            : { en: '', ar: '' },
          skuId: l.skuId,
          variantId: l.variantId,
          variantName: variant?.name,
          packaging: l.packaging,
          quantity: {
            value: submittedQuantity,
            unit: l.packaging === 'bulk' ? 'tons' : 'bags',
          },
          splitBy,
          unitPrice: { amount: safeUnitPriceAmount, currency: 'SAR' },
          deliveryOption: defaultDeliveryOption,
          isExworks: l.isExworks ?? false,
          assignee: defaultAssignee,
          resourceId: defaultResourceId,
          resourceDocumentId: defaultResourceDocumentId,
          ...(splitAdjustmentMetadata
            ? { metadata: { splitAdjustment: splitAdjustmentMetadata } }
            : {}),
          ...(deliveryOptionSplits ? { deliveryOptionSplits } : {}),
        };
      });
    }
    if (!isEdit) {
      setPendingCreatePayload(payload);
      setPendingCreateSummary(
        buildCreateConfirmationSummary(lines, skuOptions, t)
      );
      setShowCreateConfirmation(true);
      return;
    }
    onSave(payload);
  };

  const handleConfirmCreateSalesOrder = () => {
    if (!pendingCreatePayload) return;
    onSave(pendingCreatePayload);
    setPendingCreatePayload(null);
    setPendingCreateSummary(null);
    setShowCreateConfirmation(false);
  };

  // Get header title and subtitle based on mode
  const getHeaderTitle = () => {
    if (isEdit) return t('salesOrders.editSalesOrder');
    if (isReorder) return t('salesOrders.reorderTitle');
    return t('salesOrders.newSalesOrder');
  };

  const getHeaderSubtitle = () => {
    if (isEdit) return contract?.orderNumber;
    if (isReorder)
      return t('salesOrders.reorderFrom', { orderNumber: originalOrderNumber });
    return t('salesOrders.fillDetails');
  };

  const headerContent = (
    <div className={cn('flex items-center gap-3 py-2')}>
      <div className="w-10 h-10 rounded-xl bg-white/20 flex items-center justify-center">
        <FileText className="w-5 h-5 text-white" />
      </div>
      <div>
        <h2 className="text-base font-bold text-white">{getHeaderTitle()}</h2>
        <p className="text-white/70 text-xs">{getHeaderSubtitle()}</p>
      </div>
    </div>
  );

  const getButtonText = () => {
    if (isEdit) return t('common.saveChanges');
    if (isReorder) return t('salesOrders.placeReorder');
    return t('salesOrders.createSalesOrder');
  };
  const createBlockedByTenantSelection =
    !isEdit && isAdmin && !selectedCustomerTenant;
  const createBlockedMessage = t('salesOrders.selectCustomerToCreate');

  const footerContent = (
    <div className="space-y-1.5">
      {createBlockedByTenantSelection ? (
        <p className="text-xs text-muted-foreground">{createBlockedMessage}</p>
      ) : null}
      {subCustomerRequired && !selectedSubCustomer ? (
        <p className="text-xs text-muted-foreground">
          {t('salesOrders.subCustomerRequired', 'Select a sub-customer to create this sales order')}
        </p>
      ) : null}
      <div className={cn('flex gap-2')}>
        <Button
          type="button"
          data-testid="so-submit"
          onClick={handleSubmit}
          disabled={!isValid || isSaving || createBlockedByTenantSelection}
          title={
            createBlockedByTenantSelection ? createBlockedMessage : undefined
          }
          className="flex-1 h-10 rounded-xl"
        >
          {isSaving ? (
            <Loader2 className="w-4 h-4 animate-spin me-2" />
          ) : (
            <Save className="w-4 h-4 me-2" />
          )}
          {isSaving ? t('common.saving') : getButtonText()}
        </Button>
        <Button
          type="button"
          variant="outline"
          className="h-10 px-4 rounded-xl"
          onClick={() => onOpenChange(false)}
          disabled={isSaving}
        >
          {t('common.cancel')}
        </Button>
      </div>
    </div>
  );

  // Credit card form state
  const [cardForm, setCardForm] = useState({
    selectedCardId: MOCK_SAVED_CARDS[0]?.id || '',
    showNewCard: false,
    cardNumber: '',
    expiry: '',
    cvv: '',
    cardholderName: '',
  });

  // Payment method detail components
  const renderPaymentDetails = () => {
    switch (formData.paymentMethod) {
      case 'credit_limit':
        return (
          <div className="mt-3 p-4 rounded-xl bg-emerald-100 dark:bg-emerald-500/20 border border-emerald-300 dark:border-emerald-500/40">
            <div className="flex items-center justify-between mb-2">
              <span className="text-sm font-medium text-emerald-800 dark:text-emerald-300">
                {t('salesOrders.availableCredit', 'Available Balance')}
              </span>
              <span className="text-lg font-bold text-emerald-700 dark:text-emerald-300">
                SAR{' '}
                {availableCredit.toLocaleString(undefined, {
                  minimumFractionDigits: 2,
                  maximumFractionDigits: 2,
                })}
              </span>
            </div>
          </div>
        );
      case 'viban':
        return (
          <div className="mt-3 p-4 rounded-xl bg-blue-100 dark:bg-blue-500/20 border border-blue-300 dark:border-blue-500/40">
            <div className={cn('flex gap-4')}>
              {/* QR Code */}
              <div className="flex-shrink-0 p-2 bg-white dark:bg-slate-900 rounded-xl shadow-sm border border-blue-200 dark:border-blue-500/30">
                <QRCodeSVG value={MOCK_VIBAN.replace(/\s/g, '')} size={100} />
              </div>

              {/* IBAN Details */}
              <div className="flex-1 min-w-0">
                <div className="flex items-center justify-between mb-1">
                  <span className="text-xs font-semibold text-blue-800 dark:text-blue-300 flex items-center gap-1.5">
                    <QrCode className="w-3.5 h-3.5" />
                    {t('salesOrders.vibanNumber')}
                  </span>
                  <button
                    type="button"
                    onClick={copyViban}
                    className="flex items-center gap-1 text-xs font-medium text-blue-700 dark:text-blue-300 hover:underline"
                  >
                    {vibanCopied ? (
                      <Check className="w-3 h-3" />
                    ) : (
                      <Copy className="w-3 h-3" />
                    )}
                    {vibanCopied ? t('common.copied') : t('common.copy')}
                  </button>
                </div>
                <p className="font-mono text-sm font-bold text-blue-900 dark:text-blue-100 tracking-wider break-all">
                  {MOCK_VIBAN}
                </p>
                <p className="text-xs font-medium text-blue-700 dark:text-blue-400 mt-2 flex items-start gap-1.5">
                  <Info className="w-3 h-3 mt-0.5 flex-shrink-0" />
                  <span>{t('salesOrders.vibanInfo')}</span>
                </p>
              </div>
            </div>
          </div>
        );
      case 'credit_card':
        return (
          <div className="mt-3 p-4 rounded-xl bg-slate-100 dark:bg-slate-800 border border-slate-300 dark:border-slate-600 space-y-4">
            {/* Saved Cards */}
            {MOCK_SAVED_CARDS.length > 0 && !cardForm.showNewCard && (
              <div className="space-y-2">
                <Label className="text-xs font-semibold text-foreground/70">
                  {t('salesOrders.savedCards')}
                </Label>
                {MOCK_SAVED_CARDS.map((card) => (
                  <button
                    key={card.id}
                    type="button"
                    onClick={() =>
                      setCardForm((prev) => ({
                        ...prev,
                        selectedCardId: card.id,
                        showNewCard: false,
                      }))
                    }
                    className={cn(
                      'w-full flex items-center gap-3 p-3 rounded-xl border-2 transition-[background-color,border-color] bg-white dark:bg-slate-900',
                      cardForm.selectedCardId === card.id
                        ? 'border-primary bg-primary/5 dark:bg-primary/10'
                        : 'border-slate-200 dark:border-slate-700 hover:border-primary/50'
                    )}
                  >
                    {/* Card Brand Logo */}
                    <div
                      className={cn(
                        'w-10 h-7 rounded flex items-center justify-center text-[9px] font-bold text-white',
                        card.brand === 'visa' &&
                          'bg-gradient-to-r from-blue-600 to-blue-800',
                        card.brand === 'mastercard' &&
                          'bg-gradient-to-r from-red-500 to-orange-500',
                        card.brand === 'mada' &&
                          'bg-gradient-to-r from-emerald-500 to-emerald-700'
                      )}
                    >
                      {card.brand.toUpperCase()}
                    </div>
                    <div className="flex-1 text-start">
                      <p className="text-sm font-semibold text-foreground">
                        •••• •••• •••• {card.last4}
                      </p>
                      <p className="text-xs text-muted-foreground font-medium">
                        {t('salesOrders.expires')} {card.expiry}
                      </p>
                    </div>
                    {cardForm.selectedCardId === card.id && (
                      <Check className="w-4 h-4 text-primary" />
                    )}
                  </button>
                ))}

                {/* Add New Card Button */}
                <button
                  type="button"
                  onClick={() =>
                    setCardForm((prev) => ({
                      ...prev,
                      showNewCard: true,
                      selectedCardId: '',
                    }))
                  }
                  className="w-full flex items-center justify-center gap-2 p-3 rounded-xl border-2 border-dashed border-slate-300 dark:border-slate-600 hover:border-primary text-sm font-medium text-foreground/70 hover:text-foreground transition-[background-color,border-color,color]"
                >
                  <Plus className="w-4 h-4" />
                  {t('salesOrders.addNewCard')}
                </button>
              </div>
            )}

            {/* New Card Form */}
            {(cardForm.showNewCard || MOCK_SAVED_CARDS.length === 0) && (
              <div className="space-y-3">
                {MOCK_SAVED_CARDS.length > 0 && (
                  <div className="flex items-center justify-between">
                    <Label className="text-xs font-medium text-muted-foreground">
                      {t('salesOrders.newCard')}
                    </Label>
                    <button
                      type="button"
                      onClick={() =>
                        setCardForm((prev) => ({
                          ...prev,
                          showNewCard: false,
                          selectedCardId: MOCK_SAVED_CARDS[0]?.id || '',
                        }))
                      }
                      className="text-xs text-primary hover:underline"
                    >
                      {t('salesOrders.useSavedCard')}
                    </button>
                  </div>
                )}

                {/* Accepted Cards */}
                <div className="flex gap-2">
                  <div className="w-10 h-6 rounded bg-gradient-to-r from-blue-600 to-blue-800 flex items-center justify-center">
                    <span className="text-[8px] font-bold text-white">
                      VISA
                    </span>
                  </div>
                  <div className="w-10 h-6 rounded bg-gradient-to-r from-red-500 to-orange-500 flex items-center justify-center">
                    <span className="text-[8px] font-bold text-white">MC</span>
                  </div>
                  <div className="w-10 h-6 rounded bg-gradient-to-r from-emerald-500 to-emerald-700 flex items-center justify-center">
                    <span className="text-[8px] font-bold text-white">
                      MADA
                    </span>
                  </div>
                </div>

                {/* Card Number */}
                <div>
                  <Label className="text-xs">
                    {t('salesOrders.cardNumber')}
                  </Label>
                  <Input
                    value={cardForm.cardNumber}
                    onChange={(e) => {
                      const value = e.target.value
                        .replace(/\D/g, '')
                        .slice(0, 16);
                      const formatted = value.replace(/(\d{4})/g, '$1 ').trim();
                      setCardForm((prev) => ({
                        ...prev,
                        cardNumber: formatted,
                      }));
                    }}
                    placeholder="0000 0000 0000 0000"
                    className="mt-1 h-10 font-mono"
                  />
                </div>

                {/* Expiry & CVV */}
                <div className="grid grid-cols-2 gap-3 sm:gap-3">
                  <div>
                    <Label className="text-xs">{t('salesOrders.expiry')}</Label>
                    <Input
                      value={cardForm.expiry}
                      onChange={(e) => {
                        let value = e.target.value
                          .replace(/\D/g, '')
                          .slice(0, 4);
                        if (value.length >= 2)
                          value = value.slice(0, 2) + '/' + value.slice(2);
                        setCardForm((prev) => ({ ...prev, expiry: value }));
                      }}
                      placeholder="MM/YY"
                      className="mt-1 h-10 font-mono"
                    />
                  </div>
                  <div>
                    <Label className="text-xs">{t('salesOrders.cvv')}</Label>
                    <Input
                      type="password"
                      value={cardForm.cvv}
                      onChange={(e) =>
                        setCardForm((prev) => ({
                          ...prev,
                          cvv: e.target.value.replace(/\D/g, '').slice(0, 4),
                        }))
                      }
                      placeholder="•••"
                      className="mt-1 h-10 font-mono"
                    />
                  </div>
                </div>

                {/* Cardholder Name */}
                <div>
                  <Label className="text-xs">
                    {t('salesOrders.cardholderName')}
                  </Label>
                  <Input
                    value={cardForm.cardholderName}
                    onChange={(e) =>
                      setCardForm((prev) => ({
                        ...prev,
                        cardholderName: e.target.value.toUpperCase(),
                      }))
                    }
                    placeholder={t('salesOrders.cardholderNamePlaceholder')}
                    className="mt-1 h-10 uppercase"
                  />
                </div>
              </div>
            )}
          </div>
        );
      default:
        return null;
    }
  };

  return (
    <TooltipProvider delayDuration={200}>
      <BaseDrawer
        open={open}
        onOpenChange={onOpenChange}
        isRTL={isArabic}
        headerGradient="from-primary to-primary/80"
        headerContent={headerContent}
        footerContent={footerContent}
        fixedWidth={600}
        dismissible={false}
        data-testid="sales-orders-form-drawer"
      >
        <div className="space-y-6">
          {/* Basic Info Section */}
          <div className="space-y-4">
            <h3 className="text-xs font-bold uppercase tracking-wider text-foreground/50 flex items-center gap-2">
              <FileText className="w-3.5 h-3.5" />
              {t('salesOrders.basicInfo', 'Basic Information')}
            </h3>

            {/* Payment Method - B2B shows credit status, Ecommerce shows payment options */}
            {isB2B ? (
              /* B2B Credit Display */
              <div>
                <Label className="text-sm font-semibold text-foreground mb-2 block">
                  {t('salesOrders.creditStatus', 'Credit Status')}
                </Label>
                {creditLoading ? (
                  <div className="p-4 rounded-xl bg-muted/50 border border-border flex items-center justify-center gap-2">
                    <Loader2 className="w-4 h-4 animate-spin text-muted-foreground" />
                    <span className="text-sm text-muted-foreground">
                      {t('common.loading', 'Loading...')}
                    </span>
                  </div>
                ) : (
                  <div
                    className={cn(
                      'p-4 rounded-xl border',
                      summary.grandTotal > availableCredit
                        ? 'bg-amber-50 dark:bg-amber-500/10 border-amber-300 dark:border-amber-500/40'
                        : 'bg-emerald-50 dark:bg-emerald-500/10 border-emerald-300 dark:border-emerald-500/40'
                    )}
                  >
                    <div className="flex items-center justify-between mb-3">
                      <span
                        className={cn(
                          'text-sm font-medium',
                          summary.grandTotal > availableCredit
                            ? 'text-amber-800 dark:text-amber-300'
                            : 'text-emerald-800 dark:text-emerald-300'
                        )}
                      >
                        {t('salesOrders.availableCredit', 'Available Credit')}
                      </span>
                      <span
                        className={cn(
                          'text-lg font-bold',
                          summary.grandTotal > availableCredit
                            ? 'text-amber-700 dark:text-amber-300'
                            : 'text-emerald-700 dark:text-emerald-300'
                        )}
                      >
                        SAR{' '}
                        {availableCredit.toLocaleString(undefined, {
                          minimumFractionDigits: 2,
                          maximumFractionDigits: 2,
                        })}
                      </span>
                    </div>

                    {/* Progress bar: order total vs available balance */}
                    {summary.grandTotal > 0 && (
                      <div
                        className={cn(
                          'w-full h-2.5 rounded-full overflow-hidden',
                          summary.grandTotal > availableCredit
                            ? 'bg-amber-200 dark:bg-amber-500/30'
                            : 'bg-emerald-200 dark:bg-emerald-500/30'
                        )}
                      >
                        <div
                          className={cn(
                            'h-full rounded-full transition-[width]',
                            summary.grandTotal > availableCredit
                              ? 'bg-amber-500 dark:bg-amber-400'
                              : 'bg-emerald-600 dark:bg-emerald-400'
                          )}
                          style={{
                            width: `${Math.min(
                              (summary.grandTotal / (availableCredit || 1)) *
                                100,
                              100
                            )}%`,
                          }}
                        />
                      </div>
                    )}

                    <div className="flex items-center justify-end mt-2">
                      {summary.grandTotal > 0 && (
                        <p
                          className={cn(
                            'text-xs font-medium',
                            summary.grandTotal > availableCredit
                              ? 'text-amber-700 dark:text-amber-400'
                              : 'text-emerald-700 dark:text-emerald-400'
                          )}
                        >
                          {t('salesOrders.orderTotal', 'Order Total')}: SAR{' '}
                          {summary.grandTotal.toLocaleString()}
                        </p>
                      )}
                    </div>
                  </div>
                )}
              </div>
            ) : (
              /* Ecommerce Payment Method Selection */
              <div>
                <Label className="text-sm font-semibold text-foreground mb-2 block">
                  {t('salesOrders.paymentMethod')}
                </Label>
                <div className="flex flex-col sm:flex-row rounded-xl border border-border overflow-hidden">
                  {PAYMENT_METHODS.map((method) => {
                    const Icon = method.icon;
                    const isSelected = formData.paymentMethod === method.type;
                    return (
                      <button
                        key={method.type}
                        type="button"
                        onClick={() =>
                          setFormData((prev) => ({
                            ...prev,
                            paymentMethod: method.type,
                          }))
                        }
                        className={cn(
                          'flex-1 h-11 flex items-center justify-center gap-2 text-sm font-medium transition-[background-color,color]',
                          isSelected
                            ? 'bg-primary text-primary-foreground'
                            : 'hover:bg-secondary text-muted-foreground'
                        )}
                      >
                        <Icon className="w-4 h-4" />
                        <span className="truncate">{t(method.labelKey)}</span>
                      </button>
                    );
                  })}
                </div>
                {/* Payment Method Details */}
                {renderPaymentDetails()}
              </div>
            )}

            {/* Customer - editable for admin in create mode, read-only otherwise */}
            <div className="space-y-2">
              <Label className="text-sm font-semibold text-foreground">
                {t('salesOrders.customer', 'Customer')}
              </Label>
              {!isEdit && isAdmin ? (
                <div className="space-y-2">
                  <button
                    type="button"
                    data-testid="so-customer-select-admin"
                    className={cn(
                      'w-full h-11 px-3 rounded-lg border border-border bg-background text-start flex items-center justify-between',
                      isSaving && 'opacity-50 cursor-not-allowed'
                    )}
                    disabled={isSaving}
                    onClick={() => setCustomerPickerOpen((prev) => !prev)}
                  >
                    <span className="truncate text-sm">
                      {selectedCustomerTenant?.name ||
                        t(
                          'salesOrders.selectCustomerToCreate',
                          'Select a customer to create sales order'
                        )}
                    </span>
                    <ChevronDown
                      className={cn(
                        'w-4 h-4 text-muted-foreground transition-transform',
                        customerPickerOpen && 'rotate-180'
                      )}
                    />
                  </button>

                  {customerPickerOpen ? (
                    <div className="rounded-lg border border-border bg-background">
                      <div className="p-2 border-b border-border">
                        <Input
                          value={customerSearch}
                          onChange={(e) => setCustomerSearch(e.target.value)}
                          placeholder={t('common.search', 'Search')}
                          className="h-9"
                        />
                      </div>
                      <div className="max-h-52 overflow-y-auto p-1">
                        {mergedCustomerOptions.map((option) => (
                          <button
                            key={option.value}
                            type="button"
                            className={cn(
                              'w-full rounded-sm px-2 py-1.5 text-sm text-start hover:bg-accent',
                              getTenantIdentifier(selectedCustomerTenant) ===
                                option.value && 'bg-accent'
                            )}
                            onClick={() => {
                              const tenant = option.tenant ?? null;
                              setSelectedCustomerTenant(tenant);
                              dispatch(
                                adminContextActions.selectTenant(tenant)
                              );
                              setCustomerPickerOpen(false);
                            }}
                          >
                            {option.label}
                          </button>
                        ))}
                        {customersLoading ? (
                          <div className="px-2 py-1.5 text-xs text-muted-foreground">
                            {t('common.loading', 'Loading...')}
                          </div>
                        ) : null}
                        {!customersLoading &&
                        mergedCustomerOptions.length === 0 ? (
                          <div className="px-2 py-1.5 text-xs text-muted-foreground">
                            {t('common.noResults', 'No results')}
                          </div>
                        ) : null}
                      </div>
                      {customersHasMore ? (
                        <div className="p-2 border-t border-border">
                          <Button
                            type="button"
                            variant="outline"
                            className="w-full h-8"
                            onClick={(e) => {
                              e.preventDefault();
                              if (!customersLoading)
                                setCustomersPage((prev) => prev + 1);
                            }}
                            disabled={customersLoading}
                          >
                            {t('common.loadMore', 'Load more')}
                          </Button>
                        </div>
                      ) : null}
                    </div>
                  ) : null}
                </div>
              ) : null}
              {customerDisplay ? (
                <div
                  data-testid="so-customer-display"
                  className="flex items-center gap-3 p-3 rounded-lg border border-border bg-muted/30"
                >
                  <Building2 className="w-5 h-5 flex-shrink-0 text-muted-foreground" />
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium text-foreground truncate">
                      {customerDisplay.name}
                    </p>
                  </div>
                </div>
              ) : createBlockedByTenantSelection ? (
                <div
                  data-testid="so-customer-empty-admin"
                  className="p-3 rounded-lg bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800"
                >
                  <p className="text-sm text-amber-700 dark:text-amber-400 flex items-center gap-2">
                    <Info className="w-4 h-4 flex-shrink-0" />
                    {createBlockedMessage}
                  </p>
                </div>
              ) : (
                <div
                  data-testid="so-customer-empty"
                  className="flex items-center gap-3 p-3 rounded-lg border border-border bg-muted/30"
                >
                  <Building2 className="w-5 h-5 flex-shrink-0 text-muted-foreground" />
                  <p className="text-sm text-muted-foreground">—</p>
                </div>
              )}
            </div>

            {/* Plant Selection - Always shown first */}
            <div className="space-y-2">
              <Label className="text-sm font-semibold text-foreground">
                {t('salesOrders.factory', 'Plant')}
                <span className="text-destructive ms-1">*</span>
              </Label>

              {factoriesLoading ? (
                <div className="h-11 px-4 rounded-lg border border-border bg-muted/30 flex items-center gap-2">
                  <Loader2 className="w-4 h-4 animate-spin text-muted-foreground" />
                  <span className="text-sm text-muted-foreground">
                    {t(
                      'salesOrders.loadingFactories',
                      'Loading available plants...'
                    )}
                  </span>
                </div>
              ) : availableFactories.length === 0 ? (
                <div className="p-3 rounded-lg bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800">
                  <p className="text-sm text-amber-600 dark:text-amber-400 flex items-center gap-2">
                    <Info className="w-4 h-4" />
                    {t('salesOrders.noFactories', 'No plants available')}
                  </p>
                </div>
              ) : (
                <RadioGroup
                  data-testid="so-plant-group"
                  value={selectedFactoryId}
                  onValueChange={
                    isEdit
                      ? undefined
                      : (value) => {
                          setSelectedFactoryId(value);
                        }
                  }
                  className="space-y-2"
                >
                  {availableFactories.map((factory) => {
                    const factoryValue = factory.documentId || factory.id;
                    const isSelected = selectedFactoryId === factoryValue;
                    return (
                      <label
                        key={factoryValue}
                        className={cn(
                          'flex items-center gap-3 p-3 rounded-lg border transition-[background-color,border-color]',
                          isEdit
                            ? 'cursor-not-allowed opacity-80'
                            : 'cursor-pointer',
                          isSelected
                            ? 'border-primary bg-primary/5 ring-1 ring-primary'
                            : isEdit
                            ? 'border-border'
                            : 'border-border hover:border-primary/50 hover:bg-muted/50'
                        )}
                      >
                        <RadioGroupItem
                          value={factoryValue}
                          id={`factory-${factoryValue}`}
                          disabled={isEdit}
                        />
                        <Building2
                          className={cn(
                            'w-5 h-5 flex-shrink-0',
                            isSelected
                              ? 'text-primary'
                              : 'text-muted-foreground'
                          )}
                        />
                        <div className="flex-1 min-w-0">
                          <p
                            className={cn(
                              'text-sm font-medium',
                              isSelected && 'text-primary'
                            )}
                          >
                            {factory.name}
                          </p>
                        </div>
                        {isSelected && (
                          <Check className="w-5 h-5 text-primary flex-shrink-0" />
                        )}
                      </label>
                    );
                  })}
                </RadioGroup>
              )}

              {availableFactories.length > 0 && !selectedFactoryId && (
                <p className="text-xs text-destructive flex items-center gap-1">
                  <Info className="w-3 h-3" />
                  {t(
                    'salesOrders.factoryRequired',
                    'Please select a plant to continue'
                  )}
                </p>
              )}
            </div>

            {/* Delivery City - shown after plant is selected, filtered to plant's served cities */}
            {selectedFactoryId && (
              <div className="space-y-2">
                <Label className="text-sm font-semibold text-foreground">
                  {t('salesOrders.city', 'Delivery City')}
                  <span className="text-destructive ms-1">*</span>
                </Label>

                {factoryCitiesLoading ? (
                  <div className="h-11 px-4 rounded-lg border border-border bg-muted/30 flex items-center gap-2">
                    <Loader2 className="w-4 h-4 animate-spin text-muted-foreground" />
                    <span className="text-sm text-muted-foreground">
                      {t(
                        'salesOrders.loadingCities',
                        'Loading available cities...'
                      )}
                    </span>
                  </div>
                ) : deliveryCityOptions.length === 0 ? (
                  <div className="p-3 rounded-lg bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800">
                    <p className="text-sm text-amber-600 dark:text-amber-400 flex items-center gap-2">
                      <Info className="w-4 h-4" />
                      {t(
                        'salesOrders.noCitiesForFactory',
                        'No delivery cities configured for this plant'
                      )}
                    </p>
                  </div>
                ) : (
                  <Popover
                    open={cityDropdownOpen}
                    onOpenChange={(open) => {
                      setCityDropdownOpen(open);
                      if (!open) setCitySearch('');
                    }}
                  >
                    <PopoverTrigger asChild>
                      <button
                        type="button"
                        disabled={isEdit}
                        dir={isRTL ? 'rtl' : 'ltr'}
                        className={cn(
                          'h-11 w-full flex items-center justify-between px-3 rounded-lg border border-input bg-background text-sm',
                          'ring-offset-background focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2',
                          'disabled:cursor-not-allowed disabled:opacity-50',
                          'hover:bg-accent/30 transition-colors'
                        )}
                      >
                        {selectedDeliveryCity ? (
                          <span className="truncate text-foreground">
                            {selectedDeliveryCity.name}
                          </span>
                        ) : (
                          <span className="text-muted-foreground">
                            {t('salesOrders.selectCity', 'Select City')}
                          </span>
                        )}
                        <ChevronDown
                          className={cn(
                            'h-4 w-4 shrink-0 text-muted-foreground opacity-50 transition-transform',
                            cityDropdownOpen && 'rotate-180',
                            isRTL ? 'me-auto ms-2' : 'ms-auto me-2'
                          )}
                        />
                      </button>
                    </PopoverTrigger>
                    <PopoverContent
                      className="p-0"
                      align={isRTL ? 'end' : 'start'}
                      style={{
                        width: 'var(--radix-popover-trigger-width, 280px)',
                      }}
                    >
                      <Command shouldFilter={false}>
                        <CommandInput
                          placeholder={t(
                            'salesOrders.searchCity',
                            'Search city...'
                          )}
                          value={citySearch}
                          onValueChange={setCitySearch}
                          dir={isRTL ? 'rtl' : 'ltr'}
                          className={isRTL ? 'text-right' : 'text-left'}
                        />
                        <CommandList>
                          <CommandEmpty>
                            {t('salesOrders.noCityFound', 'No city found')}
                          </CommandEmpty>
                          <CommandGroup>
                            {filteredDeliveryCityOptions.map((city) => {
                              const value = city.documentId || city.id;
                              const isSelected = matchesCityOption(
                                city,
                                formData.cityId
                              );
                              return (
                                <CommandItem
                                  key={value}
                                  value={value}
                                  onSelect={() => handleCitySelection(value)}
                                  dir={isRTL ? 'rtl' : 'ltr'}
                                  className="group"
                                >
                                  <Check
                                    className={cn(
                                      'h-4 w-4 shrink-0',
                                      isRTL ? 'ml-2' : 'mr-2',
                                      isSelected ? 'opacity-100' : 'opacity-0'
                                    )}
                                  />
                                  <span>{city.name}</span>
                                  {city.nameAr && !isArabic && (
                                    <span className="ml-auto text-xs text-muted-foreground group-data-[selected=true]:text-white">
                                      {city.nameAr}
                                    </span>
                                  )}
                                  {city.nameEn &&
                                    isArabic &&
                                    city.nameEn !== city.name && (
                                      <span className="mr-auto text-xs text-muted-foreground group-data-[selected=true]:text-white">
                                        {city.nameEn}
                                      </span>
                                    )}
                                </CommandItem>
                              );
                            })}
                          </CommandGroup>
                        </CommandList>
                      </Command>
                    </PopoverContent>
                  </Popover>
                )}
              </div>
            )}

            {/* Divider */}
            <div className="border-t border-slate-200 dark:border-slate-700" />

            {/* Order Lines Section */}
            <div className="space-y-4">
              <div className={cn('flex items-center justify-between')}>
                <h3 className="text-xs font-bold uppercase tracking-wider text-foreground/50 flex items-center gap-2">
                  <Package className="w-3.5 h-3.5" />
                  {t('salesOrders.orderLines')}
                </h3>
                {canEditOrderLines && (
                  <button
                    type="button"
                    onClick={addLine}
                    className="text-sm text-primary font-medium flex items-center gap-1 hover:underline"
                  >
                    <Plus className="w-4 h-4" /> {t('products.addItem')}
                  </button>
                )}
              </div>

              <div
                className={cn('space-y-4', !canEditOrderLines && 'opacity-80')}
              >
                {lines.map((line, index) => {
                  const selectedSku = skuOptions.find(
                    (s) => s.id === line.skuId
                  );
                  const isPackagingSelected = isPackagingType(line.packaging);
                  const productOptions = filterSkuOptionsByPackaging(
                    skuOptions,
                    line.packaging
                  );
                  const canSelectProductType = isPackagingSelected;
                  const canOpenResourceDropdown =
                    canSelectResourceForLine(line);
                  const splitPlan = getSplitPlanForLine(line, skuOptions);
                  const splitCount = splitPlan.count;
                  const splitByOptions = getSplitByOptions(line.packaging);
                  const hasSplitByPresets = splitByOptions.length > 0;
                  const isCustomSplitByValue =
                    !hasSplitByPresets ||
                    !splitByOptions.includes(line.splitBy);
                  const splitByPlaceholder = getSplitByPlaceholder(
                    line.packaging,
                    t
                  );
                  const splitEditor = getSplitEditorState(line.id, splitCount);
                  const showSplitEditor =
                    splitCount > 1 && splitEditor.expanded;
                  const pageStart = splitEditor.page * SPLIT_EDITOR_PAGE_SIZE;
                  const pageQuantities = showSplitEditor
                    ? buildSplitQuantities(
                        splitPlan,
                        pageStart,
                        SPLIT_EDITOR_PAGE_SIZE
                      )
                    : [];
                  const pageEnd = showSplitEditor
                    ? Math.min(pageStart + pageQuantities.length, splitCount)
                    : 0;
                  const canGoPrev = showSplitEditor && pageStart > 0;
                  const canGoNext = showSplitEditor && pageEnd < splitCount;
                  return (
                    <div
                      key={line.id}
                      className="p-4 rounded-xl border-2 border-slate-200 dark:border-slate-700 bg-slate-50 dark:bg-slate-800/50 space-y-4"
                    >
                      {/* Header */}
                      <div className="flex items-center justify-between">
                        <span className="text-sm font-medium text-muted-foreground">
                          #{index + 1}
                        </span>
                        {canEditOrderLines && lines.length > 1 && (
                          <button
                            type="button"
                            onClick={() => removeLine(line.id)}
                            className="p-1 hover:bg-destructive/10 rounded-lg"
                          >
                            <X className="w-4 h-4 text-muted-foreground hover:text-destructive" />
                          </button>
                        )}
                      </div>

                      {/* Packaging Type */}
                      <div>
                        <Label className="text-xs font-semibold text-foreground/70 mb-2 block uppercase tracking-wide">
                          {t('products.form.packaging')}
                        </Label>
                        <div className="grid grid-cols-2 gap-2">
                          {PACKAGING_OPTIONS.map((option) => {
                            const isSelected = line.packaging === option.value;
                            return (
                              <button
                                key={option.value}
                                type="button"
                                data-testid={
                                  option.value === 'bags'
                                    ? 'so-packaging-bags'
                                    : 'so-packaging-bulk'
                                }
                                onClick={() =>
                                  updateLine(line.id, {
                                    packaging: option.value,
                                  })
                                }
                                className={cn(
                                  'h-11 px-4 rounded-xl border text-sm font-medium transition-[background-color,border-color,color]',
                                  isSelected
                                    ? 'bg-primary text-primary-foreground border-primary'
                                    : 'border-border hover:border-primary/50 text-muted-foreground'
                                )}
                              >
                                {t(option.labelKey)}
                              </button>
                            );
                          })}
                        </div>
                      </div>

                      {isPackagingSelected && (
                        <>
                          {/* Product Type + Variant - Category-Based */}
                          <div>
                            <Label className="text-xs font-semibold text-foreground/70 mb-2 block uppercase tracking-wide">
                              {t('salesOrders.productType')}
                            </Label>
                            {skuLoading || categoriesLoading ? (
                              <div className="h-11 flex items-center gap-2 text-sm text-muted-foreground">
                                <Loader2 className="w-4 h-4 animate-spin" />
                                {t('common.loading')}
                              </div>
                            ) : productOptions.length === 0 ? (
                              <div className="h-11 flex items-center text-sm text-muted-foreground">
                                {t('products.noSkus')}
                              </div>
                            ) : (
                              <TooltipProvider>
                                <div
                                  className={cn(
                                    !canSelectProductType && 'opacity-60'
                                  )}
                                >
                                  {(() => {
                                    const groupedProducts =
                                      groupProductsByCategory(
                                        productOptions,
                                        categoryOptions
                                      );

                                    return (
                                      <Accordion
                                        type="single"
                                        collapsible
                                        defaultValue={
                                          groupedProducts[0]?.category.id
                                        }
                                        className="space-y-3"
                                      >
                                        {groupedProducts.map((group) => {
                                          const color = getCategoryColorStyles(
                                            group.category
                                          );
                                          const categoryName = isRTL
                                            ? group.category.name.ar ||
                                              group.category.name.en
                                            : group.category.name.en;

                                          // Filter products by packaging
                                          const filteredProducts =
                                            group.products.filter((sku) => {
                                              const variantsForPackaging =
                                                filterVariantsByPackaging(
                                                  sku.variants,
                                                  line.packaging
                                                );
                                              return (
                                                variantsForPackaging.length > 0
                                              );
                                            });

                                          if (filteredProducts.length === 0)
                                            return null;

                                          return (
                                            <AccordionItem
                                              key={group.category.id}
                                              value={group.category.id}
                                              className="border-2 rounded-xl overflow-hidden shadow-sm"
                                              style={color.item}
                                            >
                                              <AccordionTrigger
                                                className={cn(
                                                  'px-4 py-3.5 font-semibold text-sm transition-[background-color,color] text-foreground hover:brightness-105',
                                                  '[&[data-state=open]]:rounded-b-none',
                                                  'hover:shadow-sm'
                                                )}
                                                style={color.trigger}
                                              >
                                                <div className="flex items-center gap-2.5">
                                                  <div
                                                    className="w-1.5 h-6 rounded-full"
                                                    style={color.accent}
                                                  />
                                                  <Package className="w-4 h-4" />
                                                  <span className="font-bold">
                                                    {categoryName}
                                                  </span>
                                                </div>
                                              </AccordionTrigger>
                                              <AccordionContent className="px-4 pt-3 pb-4 space-y-2.5 bg-background">
                                                {filteredProducts.map((sku) => {
                                                  const variantsForPackaging =
                                                    filterVariantsByPackaging(
                                                      sku.variants,
                                                      line.packaging
                                                    );

                                                  return variantsForPackaging.map(
                                                    (variant) => {
                                                      const isUnavailable =
                                                        canSelectProductType &&
                                                        variant.isAvailable ===
                                                          false;
                                                      const isSelected =
                                                        line.skuId === sku.id &&
                                                        matchesVariantId(
                                                          variant,
                                                          line.variantId
                                                        );
                                                      const optionKey = `${sku.id}:${variant.id}`;
                                                      const displayImageUrl =
                                                        variant.imageUrl ??
                                                        getSkuVariantFallbackImageUrl(
                                                          sku
                                                        );

                                                      const button = (
                                                        <button
                                                          type="button"
                                                          disabled={
                                                            !canSelectProductType ||
                                                            isUnavailable
                                                          }
                                                          onClick={() => {
                                                            if (
                                                              !canSelectProductType ||
                                                              isUnavailable
                                                            )
                                                              return;
                                                            updateLine(
                                                              line.id,
                                                              {
                                                                skuId: sku.id,
                                                                variantId:
                                                                  variant.id,
                                                                assignee: '',
                                                                resourceId: undefined,
                                                                resourceDocumentId: undefined,
                                                              }
                                                            );
                                                          }}
                                                          className={cn(
                                                            'w-full p-3 rounded-lg border-2 flex items-center gap-3 text-left transition-colors transition-shadow motion-safe:transition-transform motion-safe:duration-200 group',
                                                            isSelected
                                                              ? 'bg-primary text-primary-foreground border-primary shadow-sm'
                                                              : 'border-border bg-background',
                                                            canSelectProductType &&
                                                              !isUnavailable &&
                                                              !isSelected &&
                                                              'hover:border-primary/60 hover:bg-primary/5 hover:shadow-sm motion-safe:hover:-translate-y-0.5',
                                                            (!canSelectProductType ||
                                                              isUnavailable) &&
                                                              'cursor-not-allowed opacity-50'
                                                          )}
                                                        >
                                                          {/* Product Image */}
                                                          <div
                                                            className={cn(
                                                              'relative flex-shrink-0 w-12 h-12 rounded-lg flex items-center justify-center overflow-hidden transition-[transform,box-shadow,ring-color] duration-200 ring-1 ring-border/40 shadow-sm motion-safe:group-hover:scale-[1.03] motion-safe:group-hover:shadow-md group-hover:ring-primary/40',
                                                              isSelected
                                                                ? 'bg-primary-foreground/20'
                                                                : 'bg-muted group-hover:bg-primary/10'
                                                            )}
                                                          >
                                                            <span className="pointer-events-none absolute inset-0 z-10 opacity-0 transition-opacity duration-200 group-hover:opacity-100 bg-gradient-to-tr from-white/0 via-white/30 to-white/0 mix-blend-screen" />
                                                            {displayImageUrl ? (
                                                              <img
                                                                src={
                                                                  displayImageUrl
                                                                }
                                                                alt={
                                                                  variant.name
                                                                }
                                                                className="relative z-0 w-full h-full object-cover transition-transform duration-200 ease-out motion-safe:group-hover:scale-110 motion-safe:group-hover:rotate-[1deg] group-hover:brightness-105 group-hover:saturate-110"
                                                              />
                                                            ) : (
                                                              <ImageIcon
                                                                className={cn(
                                                                  'relative z-0 w-6 h-6 transition-transform duration-200 ease-out motion-safe:group-hover:scale-110 motion-safe:group-hover:rotate-[2deg] group-hover:drop-shadow-sm',
                                                                  isSelected
                                                                    ? 'text-primary-foreground/60'
                                                                    : 'text-muted-foreground group-hover:text-foreground/80'
                                                                )}
                                                              />
                                                            )}
                                                          </div>

                                                          {/* Product Info */}
                                                          <div className="flex-1 min-w-0">
                                                            <div className="font-semibold text-sm truncate">
                                                              {variant.name}
                                                            </div>
                                                            <WaitTimeLine
                                                              minutes={waitByVariant[variant.documentId ?? variant.id]}
                                                              isSelected={isSelected}
                                                            />
                                                            <div className="flex items-center gap-2 mt-0.5">
                                                              {variant.skuCode && (
                                                                <span
                                                                  className={cn(
                                                                    'text-xs',
                                                                    isSelected
                                                                      ? 'text-primary-foreground/60'
                                                                      : 'text-muted-foreground/60'
                                                                  )}
                                                                >
                                                                  {
                                                                    variant.skuCode
                                                                  }
                                                                </span>
                                                              )}
                                                            </div>
                                                          </div>

                                                          {/* Selection Indicator */}
                                                          {isSelected && (
                                                            <Check className="w-5 h-5 flex-shrink-0" />
                                                          )}
                                                        </button>
                                                      );

                                                      const hoverTrigger = (
                                                        <span className="block">
                                                          {button}
                                                        </span>
                                                      );
                                                      const hoverCard =
                                                        renderProductHoverCard({
                                                          trigger: hoverTrigger,
                                                          imageUrl:
                                                            displayImageUrl,
                                                          label: String(
                                                            variant.name ?? ''
                                                          ),
                                                          isRTL,
                                                        });

                                                      if (!isUnavailable) {
                                                        return (
                                                          <React.Fragment
                                                            key={optionKey}
                                                          >
                                                            {hoverCard}
                                                          </React.Fragment>
                                                        );
                                                      }

                                                      return (
                                                        <Tooltip
                                                          key={optionKey}
                                                        >
                                                          <TooltipTrigger
                                                            asChild
                                                          >
                                                            {hoverTrigger}
                                                          </TooltipTrigger>
                                                          <TooltipContent>
                                                            {t(
                                                              'salesOrders.variantUnavailable',
                                                              'This product is not available in selected plant'
                                                            )}
                                                          </TooltipContent>
                                                        </Tooltip>
                                                      );
                                                    }
                                                  );
                                                })}
                                              </AccordionContent>
                                            </AccordionItem>
                                          );
                                        })}
                                      </Accordion>
                                    );
                                  })()}
                                </div>
                              </TooltipProvider>
                            )}
                          </div>

                          {/* Quantity + Split By */}
                          {line.skuId &&
                            selectedSku &&
                            (() => {
                              const selectedVariant =
                                selectedSku?.variants.find((v) =>
                                  matchesVariantId(v, line.variantId)
                                );
                              if (!selectedVariant) return null;
                              const minQty = selectedVariant?.minQuantity ?? 1;
                              const maxQty = selectedVariant?.maxQuantity;
                              const quantityNum = parseInt(line.quantity) || 0;
                              const isBelowMin =
                                quantityNum > 0 && quantityNum < minQty;
                              const isAboveMax = !!(
                                maxQty && quantityNum > maxQty
                              );

                              return (
                                <>
                                  {/* Quantity with MOQ validation */}
                                  <div>
                                    <Label className="text-xs font-semibold text-foreground/70 mb-2 block uppercase tracking-wide">
                                      {t('orders.quantity')}{' '}
                                      {line.packaging === 'bulk'
                                        ? `(${t('products.tons')})`
                                        : `(${t('products.bags')})`}
                                    </Label>
                                    <Input
                                      type="number"
                                      min={minQty}
                                      max={maxQty || undefined}
                                      value={line.quantity}
                                      onChange={(e) =>
                                        updateLine(line.id, {
                                          quantity: e.target.value,
                                        })
                                      }
                                      placeholder={t(
                                        'salesOrders.quantityMinPlaceholder',
                                        'Min {{value}}',
                                        { value: minQty }
                                      )}
                                      className={cn(
                                        'h-11',
                                        (isBelowMin || isAboveMax) &&
                                          'border-destructive focus-visible:ring-destructive'
                                      )}
                                    />
                                    {(minQty > 1 || maxQty) && (
                                      <p
                                        className={cn(
                                          'text-xs mt-1',
                                          isBelowMin || isAboveMax
                                            ? 'text-destructive'
                                            : 'text-muted-foreground'
                                        )}
                                      >
                                        {isBelowMin &&
                                          t(
                                            'salesOrders.quantityMinHint',
                                            'Min: {{value}}',
                                            { value: minQty }
                                          )}
                                        {isAboveMax &&
                                          t(
                                            'salesOrders.quantityMaxHint',
                                            'Max: {{value}}',
                                            { value: maxQty }
                                          )}
                                        {!isBelowMin &&
                                          !isAboveMax &&
                                          minQty > 1 &&
                                          t(
                                            'salesOrders.quantityMinHint',
                                            'Min: {{value}}',
                                            { value: minQty }
                                          )}
                                      </p>
                                    )}
                                  </div>

                                  {/* Split By */}
                                  <div>
                                    <Label className="text-xs font-semibold text-foreground/70 mb-2 block uppercase tracking-wide">
                                      {t('salesOrders.splitBy')}
                                    </Label>
                                    <div className="flex gap-2">
                                      {hasSplitByPresets &&
                                        splitByOptions.map((opt) => (
                                          <button
                                            key={opt}
                                            type="button"
                                            onClick={() =>
                                              updateLine(line.id, {
                                                splitBy: opt,
                                              })
                                            }
                                            className={cn(
                                              'w-14 h-11 rounded-xl border text-sm font-semibold transition-[background-color,border-color,color]',
                                              line.splitBy === opt
                                                ? 'bg-primary text-primary-foreground border-primary'
                                                : 'border-border hover:border-primary/50'
                                            )}
                                          >
                                            {formatSplitByOptionLabel(
                                              opt,
                                              line.packaging,
                                              t
                                            )}
                                          </button>
                                        ))}
                                      {hasSplitByPresets ? (
                                        isCustomSplitByValue ? (
                                          <Input
                                            type="number"
                                            min="1"
                                            value={line.splitBy}
                                            onChange={(e) =>
                                              updateLine(line.id, {
                                                splitBy: e.target.value,
                                              })
                                            }
                                            placeholder={splitByPlaceholder}
                                            className="flex-1 h-11 text-center"
                                            autoFocus
                                          />
                                        ) : (
                                          <button
                                            type="button"
                                            onClick={() =>
                                              updateLine(line.id, {
                                                splitBy: '',
                                              })
                                            }
                                            className="flex-1 h-11 rounded-xl border border-border hover:border-primary/50 text-sm text-muted-foreground"
                                          >
                                            {t('common.other', 'Other')}
                                          </button>
                                        )
                                      ) : (
                                        <Input
                                          type="number"
                                          min="1"
                                          value={line.splitBy}
                                          onChange={(e) =>
                                            updateLine(line.id, {
                                              splitBy: e.target.value,
                                            })
                                          }
                                          placeholder={splitByPlaceholder}
                                          className="flex-1 h-11 text-center"
                                        />
                                      )}
                                    </div>
                                    {(selectedVariant.minOrderQuantity ||
                                      selectedVariant.maxOrderQuantity) && (
                                      <p className="text-xs text-muted-foreground mt-1">
                                        {selectedVariant.maxOrderQuantity !=
                                        null
                                          ? t(
                                              'salesOrders.orderQuantityLimitsApplied',
                                              'Order limits: {{min}} - {{max}}',
                                              {
                                                min:
                                                  selectedVariant.minOrderQuantity ??
                                                  t('common.none', 'None'),
                                                max: selectedVariant.maxOrderQuantity,
                                              }
                                            )
                                          : t(
                                              'salesOrders.orderQuantityMinLimitOnly',
                                              'Order limits: {{min}}',
                                              {
                                                min:
                                                  selectedVariant.minOrderQuantity ??
                                                  t('common.none', 'None'),
                                              }
                                            )}
                                      </p>
                                    )}
                                  </div>
                                </>
                              );
                            })()}
                        </>
                      )}
                      {/* Delivery Options with Tooltips (hidden in edit mode) */}
                      {canEditOrderLines && (
                        <div>
                          <div
                            className={cn(
                              'flex items-center justify-between mb-2'
                            )}
                          >
                            <Label className="text-xs font-semibold text-foreground/70 uppercase tracking-wide">
                              {t('salesOrders.deliveryOptions')}
                            </Label>
                            {splitCount > 1 && (
                              <button
                                type="button"
                                onClick={() =>
                                  toggleSplitEditor(line.id, !showSplitEditor)
                                }
                                className="text-[11px] font-semibold text-primary hover:underline"
                              >
                                {showSplitEditor
                                  ? t('common.close')
                                  : t('common.view')}
                              </button>
                            )}
                          </div>

                          <div>
                            {splitCount > 1 && (
                              <div
                                className={cn(
                                  'flex items-center justify-between mb-2'
                                )}
                              >
                                <span className="text-[11px] uppercase tracking-wider text-muted-foreground font-semibold">
                                  Default
                                </span>
                                <span className="text-xs text-muted-foreground flex items-center gap-1.5">
                                  {splitCount} {t('orders.title')}
                                  {splitPlan.droppedCount > 0 && (
                                    <span className="text-amber-600 dark:text-amber-400">
                                      ·{' '}
                                      {t(
                                        'salesOrders.droppedOrdersSummary',
                                        '{{count}} dropped',
                                        { count: splitPlan.droppedCount }
                                      )}
                                    </span>
                                  )}
                                </span>
                              </div>
                            )}
                            <div className="flex gap-2 items-start">
                              {/* Resource Assignment - Always visible */}
                              <div className="flex-1 space-y-2">
                                {/* Selected Resource Display */}
                                {(line.resourceId || line.resourceDocumentId) &&
                                line.assignee ? (
                                  <div className="p-3 rounded-lg bg-emerald-50 dark:bg-emerald-900/20 border border-emerald-200 dark:border-emerald-800 flex items-start justify-between gap-2">
                                    <div className="flex items-start gap-2 flex-1 min-w-0">
                                      <Check className="w-4 h-4 text-emerald-600 dark:text-emerald-400 mt-0.5 flex-shrink-0" />
                                      <div className="flex-1 min-w-0">
                                        <p className="text-xs font-semibold text-emerald-600 dark:text-emerald-400 uppercase tracking-wide mb-0.5">
                                          {t(
                                            'salesOrders.assignedResource',
                                            'Assigned Resource'
                                          )}
                                        </p>
                                        <p className="text-sm text-foreground font-medium">
                                          {line.assignee}
                                        </p>
                                      </div>
                                    </div>
                                    <button
                                      type="button"
                                      onClick={() => {
                                        updateLine(line.id, {
                                          assignee: '',
                                          resourceId: undefined,
                                          resourceDocumentId: undefined,
                                        });
                                        setResourceDropdownTarget(null);
                                        setResourceSearch(''); // Reset search
                                      }}
                                      className="text-muted-foreground hover:text-foreground transition-colors"
                                    >
                                      <X className="w-4 h-4" />
                                    </button>
                                  </div>
                                ) : (
                                  <button
                                    type="button"
                                    disabled={!canOpenResourceDropdown}
                                    onClick={() => {
                                      if (!canOpenResourceDropdown) return;
                                      const isOpen =
                                        resourceDropdownTarget?.lineId ===
                                          line.id &&
                                        resourceDropdownTarget?.splitIndex ==
                                          null;
                                      if (isOpen) {
                                        setResourceDropdownTarget(null);
                                        setShowResourceForm(false);
                                        setResourceSearch(''); // Reset search
                                      } else {
                                        setResourceDropdownTarget({
                                          lineId: line.id,
                                          splitIndex: null,
                                        });
                                        setShowResourceForm(false); // Reset form state when opening
                                        setResourceSearch(''); // Reset search
                                      }
                                    }}
                                    className={cn(
                                      'w-full h-11 px-4 rounded-lg border text-sm font-medium flex items-center justify-between transition-[background-color,border-color]',
                                      !canOpenResourceDropdown &&
                                        'opacity-50 cursor-not-allowed',
                                      resourceDropdownTarget?.lineId ===
                                        line.id &&
                                        resourceDropdownTarget?.splitIndex ==
                                          null
                                        ? 'bg-primary/5 border-primary'
                                        : 'border-border hover:border-primary/50'
                                    )}
                                  >
                                    <span
                                      className="flex items-center gap-2 text-muted-foreground"
                                      data-testid="so-resource-select"
                                    >
                                      <UserCheck className="w-4 h-4" />
                                      {t(
                                        'salesOrders.selectResource',
                                        'Select Resource'
                                      )}
                                    </span>
                                    <ChevronDown
                                      className={cn(
                                        'w-4 h-4 transition-transform',
                                        resourceDropdownTarget?.lineId ===
                                          line.id &&
                                          resourceDropdownTarget?.splitIndex ==
                                            null &&
                                          'rotate-180'
                                      )}
                                    />
                                  </button>
                                )}
                              </div>

                              {/* ExWorks Auto Badge */}
                              <Tooltip>
                                <TooltipTrigger asChild>
                                  <div
                                    data-testid="so-exw-toggle"
                                    className={cn(
                                      'h-11 px-4 rounded-xl border text-sm font-semibold flex items-center gap-2 shrink-0 transition-[background-color,border-color,color]',
                                      line.isExworks
                                        ? 'bg-orange-100 dark:bg-orange-900/30 border-orange-200 dark:border-orange-800 text-orange-600 dark:text-orange-400'
                                        : 'bg-white dark:bg-slate-900 border-border text-muted-foreground hover:border-orange-300 hover:text-orange-500'
                                    )}
                                  >
                                    <Truck className="w-4 h-4" /> EXW
                                  </div>
                                </TooltipTrigger>
                                <TooltipContent side="top">
                                  <p className="text-xs max-w-[200px]">
                                    {t(
                                      'salesOrders.exworksTooltip',
                                      'Customer picks up from plant'
                                    )}
                                  </p>
                                </TooltipContent>
                              </Tooltip>
                            </div>
                          </div>

                          {resourceDropdownTarget?.lineId === line.id &&
                            resourceDropdownTarget?.splitIndex == null &&
                            !(line.resourceId || line.resourceDocumentId) && (
                              <ResourceManagementPanel
                                requiredVehicleType={getRequiredVehicleTypeForPackaging(
                                  line.packaging
                                )}
                                selectedResourceId={line.resourceId}
                                selectedResourceDocumentId={
                                  line.resourceDocumentId
                                }
                                onSelectResource={(resource) => {
                                  updateLine(line.id, {
                                    assignee: resource.name,
                                    resourceId: resource.id,
                                    resourceDocumentId: resource.documentId,
                                  });
                                  setResourceDropdownTarget(null);
                                }}
                                onClearSelection={() => {
                                  updateLine(line.id, {
                                    assignee: '',
                                    resourceId: undefined,
                                    resourceDocumentId: undefined,
                                  });
                                }}
                                pageSize={10}
                              />
                            )}

                          {showSplitEditor && (
                            <div className="mt-3 space-y-3">
                              <div
                                className={cn(
                                  'flex items-center justify-between'
                                )}
                              >
                                <span className="text-[11px] uppercase tracking-wider text-muted-foreground font-semibold">
                                  {pageStart + 1}-{pageEnd} of {splitCount}
                                </span>
                                <div className="flex items-center gap-1">
                                  <button
                                    type="button"
                                    onClick={() =>
                                      setSplitEditorPage(
                                        line.id,
                                        splitEditor.page - 1
                                      )
                                    }
                                    disabled={!canGoPrev}
                                    title={t('common.previous')}
                                    className={cn(
                                      'h-7 w-7 rounded-lg border border-border flex items-center justify-center text-muted-foreground hover:text-foreground transition-[background-color,color]',
                                      !canGoPrev &&
                                        'opacity-40 cursor-not-allowed'
                                    )}
                                  >
                                    <ChevronLeft className="w-4 h-4" />
                                  </button>
                                  <button
                                    type="button"
                                    onClick={() =>
                                      setSplitEditorPage(
                                        line.id,
                                        splitEditor.page + 1
                                      )
                                    }
                                    disabled={!canGoNext}
                                    title={t('common.next')}
                                    className={cn(
                                      'h-7 w-7 rounded-lg border border-border flex items-center justify-center text-muted-foreground hover:text-foreground transition-[background-color,color]',
                                      !canGoNext &&
                                        'opacity-40 cursor-not-allowed'
                                    )}
                                  >
                                    <ChevronRight className="w-4 h-4" />
                                  </button>
                                </div>
                              </div>

                              {pageQuantities.map((qty, offset) => {
                                const splitIndex = pageStart + offset;
                                const split = getSplitEntry(line, splitIndex);
                                const isDropdownOpen =
                                  resourceDropdownTarget?.lineId === line.id &&
                                  resourceDropdownTarget?.splitIndex ===
                                    splitIndex;
                                const hasSelectedSplitResource = Boolean(
                                  (split.resourceId ||
                                    split.resourceDocumentId) &&
                                    split.assignee
                                );
                                return (
                                  <div
                                    key={`${line.id}-delivery-option-${splitIndex}`}
                                    className="rounded-xl border border-slate-200 dark:border-slate-700 bg-white/80 dark:bg-slate-900/30 p-3"
                                  >
                                    <div
                                      className={cn(
                                        'flex items-center justify-between'
                                      )}
                                    >
                                      <span className="text-xs font-semibold text-foreground/70">
                                        #{splitIndex + 1}
                                      </span>
                                      <span className="text-xs text-muted-foreground">
                                        {formatQuantity(qty)}
                                        {line.packaging === 'bulk'
                                          ? 'T'
                                          : ` ${t('products.bags')}`}
                                      </span>
                                    </div>
                                    <div className="mt-2 flex flex-wrap gap-2">
                                      {/* Resource Assignment - Always visible */}
                                      {hasSelectedSplitResource ? (
                                        <div className="flex-1 min-w-[180px] p-3 rounded-lg bg-emerald-50 dark:bg-emerald-900/20 border border-emerald-200 dark:border-emerald-800 flex items-start justify-between gap-2">
                                          <div className="flex items-start gap-2 flex-1 min-w-0">
                                            <Check className="w-4 h-4 text-emerald-600 dark:text-emerald-400 mt-0.5 flex-shrink-0" />
                                            <div className="flex-1 min-w-0">
                                              <p className="text-xs font-semibold text-emerald-600 dark:text-emerald-400 uppercase tracking-wide mb-0.5">
                                                {t(
                                                  'salesOrders.assignedResource',
                                                  'Assigned Resource'
                                                )}
                                              </p>
                                              <p className="text-sm text-foreground font-medium truncate">
                                                {split.assignee}
                                              </p>
                                            </div>
                                          </div>
                                          <button
                                            type="button"
                                            onClick={() => {
                                              updateSplit(line.id, splitIndex, {
                                                assignee: '',
                                                resourceId: undefined,
                                                resourceDocumentId: undefined,
                                              });
                                              setResourceDropdownTarget(null);
                                            }}
                                            className="text-muted-foreground hover:text-foreground transition-colors"
                                            title={t('common.remove', 'Remove')}
                                            aria-label={t(
                                              'common.remove',
                                              'Remove'
                                            )}
                                          >
                                            <X className="w-4 h-4" />
                                          </button>
                                        </div>
                                      ) : (
                                        <div className="flex-1 relative min-w-[180px]">
                                          <UserCheck
                                            className={cn(
                                              'absolute top-1/2 -translate-y-1/2 w-4 h-4 text-primary pointer-events-none start-3'
                                            )}
                                          />
                                          <Input
                                            value={split.assignee}
                                            onChange={(e) => {
                                              updateSplit(line.id, splitIndex, {
                                                assignee: e.target.value,
                                                resourceId: undefined,
                                                resourceDocumentId: undefined,
                                              });
                                              setResourceDropdownTarget({
                                                lineId: line.id,
                                                splitIndex,
                                              });
                                            }}
                                            onFocus={() =>
                                              setResourceDropdownTarget({
                                                lineId: line.id,
                                                splitIndex,
                                              })
                                            }
                                            onBlur={() =>
                                              setTimeout(
                                                () =>
                                                  setResourceDropdownTarget(
                                                    null
                                                  ),
                                                200
                                              )
                                            }
                                            placeholder={t(
                                              'salesOrders.resourcePlaceholder',
                                              'Search and select a resource...'
                                            )}
                                            className={cn(
                                              'h-10 border-primary focus-visible:ring-primary/30 ps-10'
                                            )}
                                          />

                                          {isDropdownOpen && (
                                            <div className="absolute z-50 w-full mt-1 bg-white dark:bg-slate-900 border-2 border-slate-200 dark:border-slate-700 rounded-xl shadow-xl max-h-48 overflow-y-auto">
                                              {resourceLoading ? (
                                                <div className="px-4 py-8 flex flex-col items-center justify-center">
                                                  {t('common.loading')}
                                                </div>
                                              ) : resourceOptions.length ===
                                                0 ? (
                                                <div className="px-4 py-8 flex flex-col items-center justify-center">
                                                  No resources found
                                                </div>
                                              ) : (
                                                resourceOptions.map(
                                                  (resource) => {
                                                    const isSelected =
                                                      (split.resourceDocumentId &&
                                                        resource.documentId &&
                                                        split.resourceDocumentId ===
                                                          resource.documentId) ||
                                                      (split.resourceId &&
                                                        split.resourceId ===
                                                          resource.id);

                                                    const secondaryParts = [
                                                      resource.driverName
                                                        ? `Driver: ${resource.driverName}`
                                                        : '',
                                                      resource.powerUnitLabel
                                                        ? `Truck: ${resource.powerUnitLabel}`
                                                        : '',
                                                      resource.trailerLabel
                                                        ? `Trailer: ${resource.trailerLabel}`
                                                        : '',
                                                    ].filter(Boolean);

                                                    return (
                                                      <button
                                                        key={
                                                          resource.documentId ??
                                                          resource.id
                                                        }
                                                        type="button"
                                                        className={cn(
                                                          'w-full px-4 py-3 text-start hover:bg-slate-100 dark:hover:bg-slate-800 flex items-start gap-3 border-b border-slate-100 dark:border-slate-800 last:border-0',
                                                          isSelected &&
                                                            'bg-primary/10 dark:bg-primary/20'
                                                        )}
                                                        onClick={() => {
                                                          if (isSelected) {
                                                            updateSplit(
                                                              line.id,
                                                              splitIndex,
                                                              {
                                                                assignee: '',
                                                                resourceId:
                                                                  undefined,
                                                                resourceDocumentId:
                                                                  undefined,
                                                              }
                                                            );
                                                            setResourceDropdownTarget(
                                                              {
                                                                lineId: line.id,
                                                                splitIndex,
                                                              }
                                                            );
                                                            return;
                                                          }

                                                          updateSplit(
                                                            line.id,
                                                            splitIndex,
                                                            {
                                                              assignee:
                                                                resource.name,
                                                              resourceId:
                                                                resource.id,
                                                              resourceDocumentId:
                                                                resource.documentId,
                                                            }
                                                          );
                                                          setResourceDropdownTarget(
                                                            null
                                                          );
                                                        }}
                                                      >
                                                        <UserCheck className="w-4 h-4 text-primary mt-0.5 flex-shrink-0" />
                                                        <div className="flex-1 min-w-0">
                                                          <p className="text-sm font-semibold text-foreground truncate">
                                                            {resource.name}
                                                          </p>
                                                          {secondaryParts.length >
                                                            0 && (
                                                            <p className="text-xs text-muted-foreground font-medium truncate">
                                                              {secondaryParts.join(
                                                                ' • '
                                                              )}
                                                            </p>
                                                          )}
                                                        </div>
                                                        {isSelected && (
                                                          <Check className="w-4 h-4 text-primary flex-shrink-0" />
                                                        )}
                                                      </button>
                                                    );
                                                  }
                                                )
                                              )}
                                            </div>
                                          )}
                                        </div>
                                      )}

                                      {/* ExWorks Auto Badge */}
                                      <Tooltip>
                                        <TooltipTrigger asChild>
                                          <div
                                            className={cn(
                                              'h-10 px-3 rounded-xl border text-xs font-semibold flex items-center gap-1.5 shrink-0 transition-[background-color,border-color,color]',
                                              split.isExworks
                                                ? 'bg-orange-100 dark:bg-orange-900/30 border-orange-200 dark:border-orange-800 text-orange-600 dark:text-orange-400'
                                                : 'bg-white dark:bg-slate-900 border-border text-muted-foreground hover:border-orange-300 hover:text-orange-500'
                                            )}
                                          >
                                            <Truck className="w-3.5 h-3.5" />{' '}
                                            EXW
                                          </div>
                                        </TooltipTrigger>
                                        <TooltipContent side="top">
                                          <p className="text-xs max-w-[200px]">
                                            {t(
                                              'salesOrders.exworksTooltip',
                                              'Customer picks up from plant'
                                            )}
                                          </p>
                                        </TooltipContent>
                                      </Tooltip>
                                    </div>
                                  </div>
                                );
                              })}
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>

            {/* Summary Table - Professional Style */}
            {summary.byProduct.length > 0 && (
              <div
                data-testid="so-order-summary"
                className="rounded-xl border-2 border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-900 overflow-hidden shadow-sm"
              >
                {/* Header */}
                <div className="px-4 py-3 border-b-2 border-slate-200 dark:border-slate-700 bg-slate-100 dark:bg-slate-800 flex items-center gap-2">
                  <ShoppingCart className="w-4 h-4 text-primary" />
                  <span className="text-sm font-bold text-foreground">
                    {t('salesOrders.orderSummary')}
                  </span>
                </div>

                {/* Table */}
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b-2 border-slate-100 dark:border-slate-800 bg-slate-50 dark:bg-slate-800/50">
                      <th
                        className={cn(
                          'px-4 py-3 font-bold text-xs uppercase tracking-wider text-foreground/70 text-start'
                        )}
                      >
                        {t('salesOrders.product')}
                      </th>
                      <th
                        className={cn(
                          'px-4 py-3 font-bold text-xs uppercase tracking-wider text-foreground/70 text-end'
                        )}
                      >
                        {t('orders.quantity')}
                      </th>
                      <th
                        className={cn(
                          'px-4 py-3 font-bold text-xs uppercase tracking-wider text-foreground/70 text-end'
                        )}
                      >
                        {t('salesOrders.generatedOrders', 'Generated Orders')}
                      </th>
                      <th
                        className={cn(
                          'px-4 py-3 font-bold text-xs uppercase tracking-wider text-foreground/70 text-end'
                        )}
                      >
                        {t('salesOrders.droppedQuantity', 'Dropped')}
                      </th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-100 dark:divide-slate-800">
                    {summary.byProduct.map((p) => (
                      <tr
                        key={p.code}
                        className="hover:bg-slate-50 dark:hover:bg-slate-800/50 transition-colors"
                      >
                        <td className="px-4 py-3">
                          <div className="flex items-center gap-2.5">
                            <div
                              className="w-3 h-3 rounded-full flex-shrink-0 ring-2 ring-white dark:ring-slate-900"
                              style={{ backgroundColor: p.color }}
                            />
                            <span className="font-semibold text-foreground">
                              {p.name}
                            </span>
                          </div>
                        </td>
                        <td
                          className={cn(
                            'px-4 py-3 font-medium text-foreground/70 text-end'
                          )}
                        >
                          {p.packaging === 'bags' && p.tonsEquivalent > 0
                            ? `${p.qty.toLocaleString()} ${
                                p.unit
                              } (${formatQuantity(p.tonsEquivalent)} ${t(
                                'products.tons'
                              )})`
                            : `${p.qty.toLocaleString()} ${p.unit}`}
                        </td>
                        <td className="px-4 py-3 font-medium text-foreground/70 text-end">
                          {formatQuantity(p.generatedQty)} {p.unit} ·{' '}
                          {p.generatedOrders}
                        </td>
                        <td className="px-4 py-3 font-medium text-amber-600 dark:text-amber-400 text-end">
                          {p.droppedQty > 0
                            ? `${formatQuantity(p.droppedQty)} ${p.unit} · ${
                                p.droppedOrders
                              }`
                            : '-'}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                  <tfoot>
                    {/* Summary Row */}
                    <tr className="border-t-2 border-slate-200 dark:border-slate-700 bg-primary/10 dark:bg-primary/20">
                      <td className="px-4 py-4">
                        <span className="text-sm font-medium text-foreground/70">
                          {summary.totalLines} {t('salesOrders.orderLines')}
                        </span>
                      </td>
                      <td
                        className={cn(
                          'px-4 py-4 text-sm font-bold text-foreground text-end'
                        )}
                      ></td>
                      <td></td>
                      <td></td>
                    </tr>
                  </tfoot>
                </table>
              </div>
            )}

            {/* Divider */}
            <div className="border-t border-slate-200 dark:border-slate-700" />

            {/* Sub-Customer (create-only; required when the tenant's requireSubCustomer is on) */}
            {!isEdit && (
              <div>
                <Label className="text-sm font-semibold text-foreground">
                  {t('salesOrders.subCustomerLabel', 'Sub-Customer')}
                  {subCustomerRequired ? (
                    <span className="text-destructive ms-1">*</span>
                  ) : (
                    <span className="text-muted-foreground font-normal">
                      {' '}({t('common.optional')})
                    </span>
                  )}
                </Label>
                {showSubCustomerSelector ? (
                  <div className="mt-1.5">
                    <SubCustomerSelector
                      currentSubCustomer={selectedSubCustomer}
                      onSelect={(sc) => {
                        setSelectedSubCustomer(sc);
                        setShowSubCustomerSelector(false);
                      }}
                      onClear={() => {
                        setSelectedSubCustomer(null);
                        setShowSubCustomerSelector(false);
                      }}
                      onCancel={() => setShowSubCustomerSelector(false)}
                      loading={false}
                      tenantDocumentId={formTenantDocumentId}
                    />
                  </div>
                ) : (
                  <div className="mt-1.5 flex items-center gap-2">
                    {selectedSubCustomer ? (
                      <>
                        <span className="text-sm text-foreground">
                          {selectedSubCustomer.name}{' '}
                          {selectedSubCustomer.code && (
                            <span className="text-muted-foreground">
                              ({selectedSubCustomer.code})
                            </span>
                          )}
                        </span>
                        <SubCustomerTypeBadge type={selectedSubCustomer.customerType} />
                        <button
                          type="button"
                          className="text-xs text-primary underline"
                          onClick={() => setShowSubCustomerSelector(true)}
                        >
                          {t('salesOrders.subCustomerChangeButton', 'Change')}
                        </button>
                        <button
                          type="button"
                          className="text-xs text-destructive underline"
                          onClick={() => setSelectedSubCustomer(null)}
                        >
                          {t('salesOrders.subCustomerClearButton', 'Remove')}
                        </button>
                      </>
                    ) : (
                      <button
                        type="button"
                        className="text-sm text-primary underline disabled:opacity-50"
                        disabled={!formTenantDocumentId}
                        onClick={() => setShowSubCustomerSelector(true)}
                      >
                        {t('salesOrders.subCustomerSelectButton', 'Select Sub-Customer')}
                      </button>
                    )}
                  </div>
                )}
              </div>
            )}

            {/* Notes */}
            <div>
              <Label className="text-sm font-semibold text-foreground">
                {t('salesOrders.notes')}{' '}
                <span className="text-muted-foreground font-normal">
                  ({t('common.optional')})
                </span>
              </Label>
              <Textarea
                value={formData.notes}
                onChange={(e) =>
                  setFormData((prev) => ({ ...prev, notes: e.target.value }))
                }
                placeholder={t('salesOrders.notesPlaceholder')}
                className="mt-1.5 min-h-[80px]"
              />
            </div>
          </div>
        </div>
      </BaseDrawer>
      <AlertDialog
        open={showCreateConfirmation}
        onOpenChange={(openState) => {
          setShowCreateConfirmation(openState);
          if (!openState) {
            setPendingCreatePayload(null);
            setPendingCreateSummary(null);
          }
        }}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>
              {t('salesOrders.confirmCreateSalesOrderTitle')}
            </AlertDialogTitle>
            <AlertDialogDescription>
              {t('salesOrders.confirmCreateSalesOrderDescription')}
            </AlertDialogDescription>
          </AlertDialogHeader>
          {pendingCreateSummary && (
            <div className="space-y-3 text-sm">
              <div className="grid grid-cols-3 gap-2 rounded-lg border border-border bg-muted/30 p-3">
                <div>
                  <p className="text-xs text-muted-foreground">
                    {t('salesOrders.requestedQuantity', 'Requested')}
                  </p>
                  <p className="font-semibold">
                    {formatQuantity(
                      pendingCreateSummary.totalRequestedQuantity
                    )}
                  </p>
                </div>
                <div>
                  <p className="text-xs text-muted-foreground">
                    {t('salesOrders.generatedOrders', 'Generated Orders')}
                  </p>
                  <p className="font-semibold">
                    {pendingCreateSummary.totalGeneratedOrders}
                  </p>
                </div>
                <div>
                  <p className="text-xs text-muted-foreground">
                    {t('salesOrders.droppedQuantity', 'Dropped')}
                  </p>
                  <p className="font-semibold text-amber-600 dark:text-amber-400">
                    {formatQuantity(pendingCreateSummary.totalDroppedQuantity)}
                  </p>
                </div>
              </div>
              <div className="max-h-60 space-y-2 overflow-y-auto">
                {pendingCreateSummary.lines.map((line) => (
                  <div
                    key={line.lineId}
                    className="rounded-lg border border-border p-3"
                  >
                    <div className="flex items-start justify-between gap-3">
                      <div>
                        <p className="font-medium text-foreground">
                          {line.productName}
                        </p>
                        <p className="text-xs text-muted-foreground">
                          {formatQuantity(line.generatedQuantity)} {line.unit} ·{' '}
                          {line.generatedOrders} {t('orders.title')}
                        </p>
                      </div>
                      {line.droppedQuantity > 0 && (
                        <span className="rounded-full bg-amber-100 px-2 py-1 text-xs font-semibold text-amber-700 dark:bg-amber-900/30 dark:text-amber-300">
                          {formatQuantity(line.droppedQuantity)} {line.unit}{' '}
                          {t('salesOrders.dropped', 'dropped')}
                        </span>
                      )}
                    </div>
                    {(line.minOrderQuantity || line.maxOrderQuantity) && (
                      <p className="mt-2 text-xs text-muted-foreground">
                        {line.maxOrderQuantity != null
                          ? t(
                              'salesOrders.orderQuantityLimitsApplied',
                              'Order limits: {{min}} - {{max}}',
                              {
                                min:
                                  line.minOrderQuantity ??
                                  t('common.none', 'None'),
                                max: line.maxOrderQuantity,
                              }
                            )
                          : t(
                              'salesOrders.orderQuantityMinLimitOnly',
                              'Order limits: {{min}}',
                              {
                                min:
                                  line.minOrderQuantity ??
                                  t('common.none', 'None'),
                              }
                            )}
                      </p>
                    )}
                  </div>
                ))}
              </div>
            </div>
          )}
          <AlertDialogFooter>
            <AlertDialogCancel>{t('common.cancel')}</AlertDialogCancel>
            <AlertDialogAction onClick={handleConfirmCreateSalesOrder}>
              {t('salesOrders.createSalesOrder')}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </TooltipProvider>
  );
}

export const SalesOrderFormDrawer = React.memo(SalesOrderFormDrawerInner);
